import { v1 as uuid } from "uuid";
import {
  AuditActionTypes,
  AvailabilityDayType,
  AvailabilityType,
  CountType,
  EventDay,
  EventType,
  HygieneBreak,
  ItemTypes,
  JavascriptDay,
  LockCountType,
  LockTypes,
  OwnerType,
  PenaltyType,
  PendingUpdateType,
  PreselectType,
  ProfileRole,
  ReportStyle,
  Strictness,
  TaskType,
  TraceLevel,
  WeekendHandling,
} from "../ts/enums";
import {
  endOfToday,
  isAfter,
  isBefore,
  isToday,
  isTomorrow,
  startOfYesterday,
} from "date-fns";
import {
  PREVIEW_EVENT_TASKS,
  STATE_KEY_HISTORY_CREDITS,
  STATE_KEY_OVERRIDE_TIMES,
} from "./StatefulSettings";
import Database, { defaultSettings } from "../utilities/database";
import Cookies from "../utilities/cookies";
import debounce from 'lodash/debounce';
import round from 'lodash/round';
import { mergeSettings, stripSettings } from "../utilities/settingsManager";
import {
  MessageType,
  NotificationType,
  sendExceptionMessage,
  sendItemRequestSettingsChangeToOwner,
  sendMessageToMember,
  sendPilloryAddedMessageToOwner,
} from "./messaging";
import ConfigCache from "../utilities/ConfigCache";
import {
  canHandleDiscordCalls,
  handleDiscordPilloryTicker,
} from "../utilities/discord";
import { decryptWithAES, encryptWithAES } from "../utilities/cryptoUtils";
import {
  IControl,
  ISessionSettings,
  IOwnerProfileSession,
  AuditMessage,
  IUserRecord,
  ControlRecord,
  IShowNotificationState,
  ISettingsItem,
  IBasket,
  ISessionProgressState,
  IOwnershipControl,
  Holiday,
  BaseItem,
  ITaskItem,
  IEventItem,
  IPermissionItem,
  IPenaltyItem,
  IGeneralSettings,
  ITaskSettings,
  IPenaltySettings,
  IPositionSettings,
  IPermissionSettings,
  IEventSettings,
  PositionItem,
  EventItem,
  PermissionItem,
  PenaltyItem,
  TaskItem,
  UserActionAuditMessage,
  ITaskBasketInfo,
  ICategoryItem,
  ITaskTimerInfo,
  IPenaltyTimerInfo,
  IPendingItem,
  IOwnerRequired,
  IItemSupport,
  OwnedItemsCollection,
  LovenseToy,
  ItemAdditionOptions,
  TaskBasketItem,
  Item,
  PenaltyBasketItem,
  IScheduledInfo,
  ISchedulerTimerInfo,
  VoteTask,
  IDaySchedule,
  ICustomSchedule,
  IScheduledItem,
  IShoppingRequest,
  Task,
  ISiteEffectsSettings,
  ILinesRequest,
  AdditionDialogResponse,
  ILovenseToy,
  PilloryTopic,
  IPictureItem,
  ICachedRandomSelectionProvider,
  IOptionSupport,
  INamedItem,
  defaultSodEvent,
  ISystemConfig,
} from "../ts/Definitions";
import {
  roughSizeOfObject,
  decrypt,
  getOwnerUserId,
  memberOwnerKeySuffix,
  cloneObject,
  diffObjects,
  getItemTypeDisplayName,
  isAvailableItem,
  getRandomItemWithCache,
  getCountMinutes,
  isTimedItem,
  getRandomItem,
  convertToCountTypeString,
  convertFromMinutesToCountType,
  updateArray,
  isOutfitCategory,
} from "../utilities/utils";
import BaseSettings from "./BaseSettings";
import {
  convertToEventDayFromDate,
  isWeekendDay,
} from "../utilities/dateUtils";
import Emitter from "../utilities/Emitter";

// pure settings only features for ability to edit and save session settings
// without any state or other database features or en

import { AuditService } from '../services/AuditService';
import { ItemRepository } from '../services/ItemRepository';
import { SubscriptionManager, SubscriptionType, ListenerType } from '../services/SubscriptionManager';
import { CategoryHelper } from '../services/CategoryHelper';
import { ScheduleManager, ScheduleManagerCallbacks } from '../services/ScheduleManager';
import { BasketService, BasketServiceCallbacks } from '../services/BasketService';

// TEMPORARY: cold-start load performance logging — remove before next deploy
const loadPerf = (label: string, ...extra: unknown[]) => {
  if (typeof performance === 'undefined') return;
  if (import.meta.env.VITE_ENABLE_LOAD_PERF !== 'true') return;
  const g = globalThis as any;
  if (!g.__loadPerfT0) g.__loadPerfT0 = performance.now();
  console.log(`[LOAD_PERF] +${(performance.now() - g.__loadPerfT0).toFixed(0)}ms | ${label}`, ...extra);
};

export const RANDOM_GAME_OF_TYPE_PREFIX = "RANDOM_GAME_OF_TYPE|";

export default class Settings
  extends BaseSettings
  implements ICachedRandomSelectionProvider {
  private allControlRecords: Record<string, ControlRecord> = {};
  protected systemSettings?: ISessionSettings;
  protected cookies: Cookies;
  protected ownerSettings?: ISessionSettings;
  public showMessage: (args: IShowNotificationState) => void;
  private awaitingSystemSettings = true;
  private awaitingSettings = true;
  private awaitingOwnerSettings = true;
  protected systemUserId = "";
  private awaitingControl = true;
  protected mergedSettings = false;
  private migrated = false;
  // guards getStartOfDayEvent (called from dateUtils on nearly every render/tick) against
  // repeatedly creating and writing a default start-of-day event if it isn't yet visible
  // to getEvents() on the next call (e.g. a settings snapshot race).
  private pendingStartOfDayEvent?: IEventItem;
  private startOfDayEventCreatedAt = 0;
  private static readonly START_OF_DAY_EVENT_CREATION_COOLDOWN_MS = 60_000;
  protected controlRecord: ControlRecord;
  protected memberOwnerProfileSummary?: IOwnerProfileSession;
  protected ownerProfileSummary?: IOwnerProfileSession;
  protected memberOwnerProfileSettings?: ISessionSettings;
  protected settings: ISessionSettings = {} as ISessionSettings;
  private ownerSettingsSubscriptionUserId?: string;
  private configCache: ConfigCache;
  protected config: ISystemConfig | null = null;
  protected auditService: AuditService;
  protected itemRepository: ItemRepository;
  protected subscriptionManager: SubscriptionManager;
  protected categoryHelper: CategoryHelper;
  protected scheduleManager: ScheduleManager;
  protected basketService: BasketService;

  protected constructor(
    database: Database,
    showMessage: (args: IShowNotificationState) => void,
    cookies: Cookies,
    settingsUserId: string,
    cache: ConfigCache
  ) {
    super(database);
    this.configCache = cache;
    this.configCache.fetchConfig().then(config => this.config = config);
    this.cookies = cookies;
    this.database = database;
    this.showMessage = showMessage;
    this.controlRecord = new ControlRecord({
      userid: settingsUserId,
    } as IControl);
    this.auditService = new AuditService(
      database,
      () => this.getControl(),
      showMessage
    );
    this.itemRepository = new ItemRepository(
      this.auditService,
      () => this.isSystemSettings(),
      (item) => this.getCurrentItem(item),
      (item, bypass) => this.validateItem(item, bypass),
      () => this.fireListenersInner()
    );
    this.subscriptionManager = new SubscriptionManager();
    this.categoryHelper = new CategoryHelper();
    this.scheduleManager = new ScheduleManager(this.createScheduleCallbacks());
    this.basketService = new BasketService(this.createBasketCallbacks());
  }

  private createScheduleCallbacks(): ScheduleManagerCallbacks {
    return {
      getTasks: (filter) => this.getTasks(filter),
      getEvents: (filter) => this.getEvents(filter),
      getPermissions: (filter) => this.getPermissions(filter),
      getPenalties: (filter) => this.getPenalties(filter),
      getTask: (key) => this.getTask(key),
      getEvent: (key) => this.getEvent(key),
      getPermission: (key) => this.getPermission(key),
      getPenalty: (key) => this.getPenalty(key),
      getTaskSettings: () => this.getTaskSettings(),
      getEventSettings: () => this.getEventSettings(),
      getPermissionSettings: () => this.getPermissionSettings(),
      getSettings: () => this.settings,
      canAddTask: (item, date, bypass) => this.canAddTask(item, date, bypass),
      itemAvailableForDay: (item, day, hidden, ignore) =>
        this.itemAvailableForDay(item, day, hidden, ignore),
      itemAvailableForDayDiary: (item, day) =>
        this.itemAvailableForDayDiary(item, day),
      canPerformSomeTasksForEventDay: (day) =>
        this.canPerformSomeTasksForEventDay(day),
      canPerformAllTasksForEventDay: (day) =>
        this.canPerformAllTasksForEventDay(day),
      checkHolidayToday: () => this.checkHolidayToday(),
      getTaskForCategory: (cat, item, date, test) =>
        this.getTaskForCategory(cat, item, date, test),
      getScheduledCustomSchedule: (day, date) => this.getScheduledCustomSchedule(day, date),
      getBusyDays: () => this.getBusyDays(),
      peekWeekendDays: () => this.peekWeekendDays(),
      describeTask: (t, day, from, cond, opts, keys, choice) =>
        this.describeTask(t, day, from, cond, opts, keys, choice),
    };
  }

  private createBasketCallbacks(): BasketServiceCallbacks {
    return {
      getTask: (key) => this.getTask(key),
      getPenalty: (key) => this.getPenalty(key),
      getEvent: (key) => this.getEvent(key),
      getPermission: (key) => this.getPermission(key),
      getTasks: (predicate) => this.getTasks(predicate),
      getGeneralSettings: () => this.settings,
      getTaskSettings: () => this.getTaskSettings(),
      getTaskSchedule: () => this.getTaskSchedule(),
      getScheduleInfo: () => this.getScheduleInfo(),
      canAddTask: (item, date, testRun) => this.canAddTask(item, date, testRun),
      checkItemIsOwned: (item) => this.checkItemIsOwned(item),
      itemAvailableForDay: (item, eventDay, includeHidden, ignoreAvailability) =>
        this.itemAvailableForDay(item, eventDay, includeHidden, ignoreAvailability),
      getState: (key, defaultVal) => this.getState(key, defaultVal),
      isTodayPreviewDay: () => this.isTodayPreviewDay(),
      getPreviewChoicesForItem: (item) => this.getPreviewChoicesForItem(item),
      shouldReplaceRoutineItemOnPreview: (task) => this.shouldReplaceRoutineItemOnPreview(task),
      getPreviousItemSelectionState: () => this.getPreviousItemSelectionState(),
      updatePreviousItemSelections: (key, vals, add) => this.updatePreviousItemSelections(key, vals, add),
      createPilloryTopic: (options) => this.createPilloryTopic(options),
      auditUser: (message) => this.auditUser(message),
      getItemOptionSelectedToday: (item) => this.getItemOptionSelectedToday?.(item),
    };
  }

  public getPreviousItemSelectionState = (): Record<string, string[]> => ({});

  public updatePreviousItemSelections = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _key: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _vals: string[],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _add: boolean
  ): void => {
    return;
  };

  public getConfig = () => this.configCache;

  public dispose() {
    // Dispose subscription manager (includes firing disposed listeners)
    this.subscriptionManager?.dispose();

    // Dispose other services
    this.auditService?.dispose();

    // Cancel debounced functions
    this.debouncedUpdateControl?.cancel?.();
    this.fireListeners?.cancel?.();
  }

  protected updateControlListeners(): void {
    this.setLoaded(false);
    if (this.subscriptionManager.hasListeners(ListenerType.AllControlRecords)) {
      this.subscriptionManager.fireListenersSync(
        ListenerType.AllControlRecords,
        Object.values(this.allControlRecords)
      );
    }
  }

  /**
   * is the settings loaded with enough information for the initial render whilst waiting for other records to come in
   * @returns
   */
  public isControlLoaded = (): boolean => !this.awaitingControl;

  protected setAwaitingOwnerSettings(value: boolean): void { this.awaitingOwnerSettings = value; }
  protected setAwaitingControl(value: boolean): void { this.awaitingControl = value; }

  /**
   * do we have all the data loaded to determine the settings for the user
   * @returns
   */
  public isSettingsLoaded = (): boolean =>
    !this.awaitingSettings && !this.awaitingSystemSettings;

  public getSystemUser = () => this.database.getSystemUser();

  public isSystemLoggedOn = (): boolean => {
    return this.cookies.userid === this.systemUserId;
  };

  public isSystemSettings = (): boolean =>
    this.controlRecord &&
    this.systemUserId !== "" &&
    this.controlRecord.userid !== undefined &&
    this.controlRecord.userid.startsWith(this.systemUserId);

  protected isSessionOwnedByLoggedOnUser = (): boolean => {
    return this.controlRecord.isSettingsOwner(
      this.cookies,
      this.isSystemLoggedOn()
    );
  };

  public isLoggedOnUserSession = (): boolean => {
    return this.controlRecord.userid === this.cookies.userid;
  };

  public getOwnerProfileSettings = (
    shouldMergeSettings: boolean
  ): ISessionSettings | undefined => {
    if (shouldMergeSettings)
      return mergeSettings(
        this.memberOwnerProfileSettings as ISessionSettings,
        undefined,
        this.systemSettings,
        false
      );
    return this.memberOwnerProfileSettings;
  };

  public addDisposedListener = (cb: () => void) => {
    this.subscriptionManager.subscribe(ListenerType.Disposed, cb);
  };

  public unsubscribeAll(): void {
    this.subscriptionManager.unsubscribe(ListenerType.ControlRecordChanged);
    this.subscriptionManager.unsubscribe(ListenerType.ViewChange);
    this.subscriptionManager.unsubscribe(ListenerType.Loaded);
  }

  public subscribeToLoaded = (listener: () => void): void => {
    this.subscriptionManager.subscribe(ListenerType.Loaded, listener);
  };

  public unsubscribeToLoaded = (listener: () => void): void => {
    this.subscriptionManager.unsubscribe(ListenerType.Loaded, listener);
  };

  public unsubscribeToAllControlRecords = (
    listener?: () => void
  ): void => {
    this.subscriptionManager.unsubscribe(ListenerType.AllControlRecords, listener);
  };

  public subscribeToAllControlRecords = (listener: () => void): void => {
    this.subscriptionManager.subscribe(ListenerType.AllControlRecords, listener);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isLoaded() {
    return (
      !this.awaitingSettings &&
      !this.awaitingSystemSettings &&
      !this.awaitingOwnerSettings
    );
  }

  public isAdmin = (): boolean => {
    return this.controlRecord.isAdmin || this.isSystemLoggedOn();
  };

  public setAdminMode = (isAdmin: boolean): void => {
    void this.updateControlAttributes({ isAdmin });
  };

  private updateControlInner = (): void => {
    if (this.isLoggedOnUserSession() || this.isSystemLoggedOn()) {
      const localControl = this.pendingControlUpdate;
      this.pendingControlUpdate = undefined;
      if (localControl) {
        void this.database.updateControl(
          this.controlRecord.userid,
          localControl,
          this.isSystemLoggedOn()
        );

        if (this.subscriptionManager.hasListeners(ListenerType.ControlRecordChanged)) {
          this.subscriptionManager.fireListenersSync(
            ListenerType.ControlRecordChanged,
            localControl
          );
        }
      }
    }
  };

  private pendingControlUpdate: ControlRecord | undefined;
  private updateControlRecord = (control: ControlRecord) => {
    this.pendingControlUpdate = new ControlRecord({ ...control });
    this.debouncedUpdateControl();
  };

  private debouncedUpdateControl = debounce(this.updateControlInner, 1000, {
    leading: false,
    trailing: true,
  });

  public updateControlAttributes = (
    controlUpdates: Partial<IControl>
  ): void => {
    this.controlRecord = new ControlRecord({
      ...this.controlRecord,
      ...controlUpdates,
    });
    this.updateControlRecord(this.controlRecord);
  };

  protected deleteFromCollection(
    items: ISettingsItem[],
    itemKey: string,
    updateSettings: (items: ISettingsItem[]) => void
  ): ISettingsItem | undefined {
    const match = items.findIndex((i) => i.key === itemKey);
    if (match === -1) return undefined;
    const item = items[match];
    if (this.isSystemLoggedOn() && item.isDeleted) {
      items.splice(match, 1);
    } else {
      item.isDeleted = true;
    }
    updateSettings(items);
    this.fireListeners();
    return item;
  }

  private fireListenersInner = (): void => {
    if (this.subscriptionManager.hasListeners(ListenerType.ViewChange)) {
      void this.subscriptionManager.fireListeners(ListenerType.ViewChange, this);
    }
  };

  public fireListeners = debounce(this.fireListenersInner, 500);

  public subscribeToControlRecord = (
    listener: (control: ControlRecord) => void
  ): void => {
    this.subscriptionManager.subscribe(ListenerType.ControlRecordChanged, listener);
  };

  public unsubscribeToControlRecord = (
    listener: (control: ControlRecord) => void
  ): void => {
    this.subscriptionManager.unsubscribe(ListenerType.ControlRecordChanged, listener);
  };

  public subscribeToViewChanges = (
    listener?: (settings: Settings) => void
  ): void => {
    if (listener) {
      this.subscriptionManager.subscribe(ListenerType.ViewChange, listener);
    }
  };

  public getProgressStatus = () => "";

  public getBankBalance = (): number => 0;

  public isTomorrowAPreviewDay = (): boolean => false;

  public isTodayPreviewDay = (): boolean => false;

  public getCheckoutPercent = (): number => 0;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public getBasketItems = (includePending: boolean): IBasket => ({
    tasks: [],
    penalties: [],
    pendingItems: [],
    permissions: [],
    positions: [],
  });

  public unsubscribeToViewChanges = (
    listener?: (settings: Settings) => void
  ): void => {
    this.subscriptionManager.unsubscribe(ListenerType.ViewChange, listener);
  };

  protected setLoaded(settingChange: boolean): void {
    if (this.isLoaded() || settingChange) {
      if (
        !this.awaitingOwnerSettings &&
        !this.awaitingSystemSettings &&
        !this.awaitingSettings &&
        !this.migrated
      ) {
        const preMigrationSnapshot = JSON.stringify(this.settings ?? {});
        this.settings = Database.migrateSettings(
          this.controlRecord.userid,
          this.settings,
          this.database,
          this.ownerSettings ?? this.systemSettings
        );
        this.migrated = true;

        const strippedSettings = stripSettings(
          this.settings,
          this.ownerSettings,
          this.systemSettings,
          this.isSystemSettings(),
          this.controlRecord.userid,
          this.database
        );
        const postMigrationSnapshot = JSON.stringify(strippedSettings ?? {});
        const currentSize = roughSizeOfObject(this.settings);
        const newSize = roughSizeOfObject(strippedSettings);
        if (preMigrationSnapshot !== postMigrationSnapshot || currentSize > newSize) {
          void this.database.updateSettings(
            this.controlRecord.userid,
            strippedSettings,
            true
          );
          this.settings = strippedSettings;
        }
      }
      if (this.migrated && settingChange) {
        const strippedSettings = stripSettings(
          this.settings,
          this.ownerSettings,
          this.systemSettings,
          this.isSystemSettings(),
          this.controlRecord.userid,
          this.database
        );
        // we Pneed to strip down first so we don't merge merged settings on to itself which basically treats all items as custom
        this.settings = mergeSettings(
          strippedSettings,
          this.ownerSettings,
          this.systemSettings,
          this.isSystemSettings()
        );
        if (!this.mergedSettings) {
          this.mergedSettings = true;
        }
      }
    }

    if (this.isLoaded()) {
      if (this.subscriptionManager.hasListeners(ListenerType.Loaded)) {
        loadPerf(`Settings.setLoaded → isLoaded() TRUE → firing Loaded listeners`);
        this.subscriptionManager.fireListenersSync(ListenerType.Loaded);
        // Clear loaded listeners after firing once
        this.subscriptionManager.unsubscribe(ListenerType.Loaded);
      }
    }
    this.fireListeners();
  }

  private cachedCommunityProfiles: Record<string, ControlRecord> = {};

  private get communityProfiles() {
    if (!this.cachedCommunityProfiles) this.cachedCommunityProfiles = {};
    return this.cachedCommunityProfiles;
  }

  public getCommunityProfiles = (): ControlRecord[] => {
    return Object.values(this.communityProfiles).sort((x, y) =>
      (x.username ?? '').localeCompare(y.username ?? '')
    );
  };

  public getCommunityProfile = (userid: string): ControlRecord => {
    return this.communityProfiles[userid];
  };

  protected handleAllControlsChanged(record: ControlRecord): void {
    if (!record) return;
    if (!this.allControlRecords) this.allControlRecords = {};
    this.allControlRecords[record.userid] = record;

    if (
      (!this.isSystemLoggedOn() &&
        record.userid === this.database.getSystemUser()?.userid) ||
      !record.username
    )
      return;

    const profile = this.getMemberCommunityProfile(record);
    if (!profile.username) return;
    if (!this.cachedCommunityProfiles) this.cachedCommunityProfiles = {};
    this.cachedCommunityProfiles[record.userid] = profile;

    if (record.userid === this.controlRecord.userid)
      this.controlRecordChangedCallback(record);
    this.updateControlListeners();
  }

  /**
   * Called after systemUserId has been resolved and assigned.
   * Subclasses can override to react when isSystemLoggedOn() first becomes reliable.
   */
  protected onAfterSystemUserIdLoaded(): void {
    // no-op in base class
  }

  protected onAllControlsChanged = (record: ControlRecord): void => {
    this.handleAllControlsChanged(record);
  };

  private systemSettingsChangedCallback = (newSettings: ISessionSettings): void => {
    if (newSettings) {
      this.systemSettings = newSettings;
    }
    this.awaitingSystemSettings = false;
    loadPerf(`Settings.systemSettingsChanged (awaitingSystemSettings cleared)`, { bytes: JSON.stringify(newSettings ?? {}).length });
    this.setLoaded(true);
  };

  private ensureOwnerSettingsSubscription = (): void => {
    if (!this.controlRecord || this.controlRecord.getOwnerType() === OwnerType.unowned) {
      this.ownerSettingsSubscriptionUserId = undefined;
      this.ownerSettings = undefined;
      this.awaitingOwnerSettings = false;
      this.subscriptionManager.unsubscribeFrom(SubscriptionType.OwnerSettings);
      return;
    }

    const ownerSettingsUserId = getOwnerUserId(this.controlRecord.getOwner().userid);
    if (!ownerSettingsUserId || this.ownerSettingsSubscriptionUserId === ownerSettingsUserId) {
      return;
    }

    this.ownerSettingsSubscriptionUserId = ownerSettingsUserId;
    this.awaitingOwnerSettings = true;
    const ownerSettingsUnsubscribe = this.database.snapAndSubSettings(
      ownerSettingsUserId,
      this.ownerSettingsChangedCallback,
      { disableLegacyFallback: true, readinessTimeoutMs: 1500 }
    );
    this.subscriptionManager.registerUnsubscribe(SubscriptionType.OwnerSettings, ownerSettingsUnsubscribe);
    void this.database
      .snapOwnerProfile(ownerSettingsUserId)
      .then(this.ownerProfileSummaryChangedCallback);
  };

  protected initialise(userid: string) {
    loadPerf(`Settings.initialise start`);
    void this.database
      .snapControlRecord(userid)
      .then((controlRecord) =>
        this.controlRecordChangedCallback(controlRecord)
      );

    void this.database.lookupSystemUser().then((user) => {
      this.systemUserId = user.userid;
      this.onAfterSystemUserIdLoaded();
      const unsubscribe = this.database.snapAndSubSettings(
        this.systemUserId,
        this.systemSettingsChangedCallback,
        { disableLegacyFallback: true, readinessTimeoutMs: 1500 }
      );
      this.subscriptionManager.registerUnsubscribe(SubscriptionType.SystemSettings, unsubscribe);
    });
    // One-shot: load the initial settings then unsubscribe so subsequent owner writes
    // to settingsV2/{userid} don't replay the full section back to this session.
    // Live updates are delivered instead via PendingUpdateType.settingsItemUpdate /
    // refreshSettings in the pending state channel.
    let userSettingsUnsubscribe: (() => void) | undefined;
    let initialUserSettingsDelivered = false;
    userSettingsUnsubscribe = this.database.snapAndSubSettings(
      userid,
      (s) => {
        if (initialUserSettingsDelivered) return;
        initialUserSettingsDelivered = true;
        this.onSettingsChanged(s);
        // Callback can fire synchronously before userSettingsUnsubscribe is assigned.
        // Defer unsubscribe to guarantee the returned function is available.
        queueMicrotask(() => userSettingsUnsubscribe?.());
      },
      { disableLegacyFallback: true, readinessTimeoutMs: 1500 }
    );
    this.subscriptionManager.registerUnsubscribe(SubscriptionType.UserSettings, () => userSettingsUnsubscribe?.());

    void this.database
      .snapOwnerProfile(getOwnerUserId(userid))
      .then((profile) =>
        this.memberOwnerProfileSummaryChangedCallback(profile)
      );

    void this.database
      .getUserSettings(getOwnerUserId(userid))
      .then((userSettings) => {
        if (userSettings)
          this.ownerProfileSettingsChangedCallback(userSettings);
      });
    this.database.snapAndSubAllControl(this.onAllControlsChanged);
    const ownershipUnsubscribe = this.database.snapAndSubOwnershipControl(
      userid,
      this.onOwnershipChanged
    );
    this.subscriptionManager.registerUnsubscribe(SubscriptionType.OwnershipControl, ownershipUnsubscribe);

    const sessionProgressUnsubscribe = this.database.snapAndSubSessionProgressState(
      userid,
      this.onSessionProgressStateChanged
    );
    this.subscriptionManager.registerUnsubscribe(SubscriptionType.SessionProgressState, sessionProgressUnsubscribe);
  }

  public getAllControlRecords = (): ControlRecord[] => {
    return Object.values(this.allControlRecords).filter(
      (p) => p.username !== import.meta.env.VITE_GUEST_NAME
    );
  };

  public getProfile = (userid: string): ControlRecord | undefined => {
    if (userid && userid.endsWith(memberOwnerKeySuffix))
      userid = userid.replace(memberOwnerKeySuffix, "");
    return this.allControlRecords[userid];
  };

  protected controlRecordChangedCallback = (user: IControl): void => {
    if (!user) return;
    const newControlRecord = new ControlRecord(user as IControl);
    Database.migrateControl(newControlRecord);
    this.controlRecord = newControlRecord;
    this.ensureOwnerSettingsSubscription();

    this.awaitingControl = false;
    loadPerf(`Settings.controlRecordChanged`, { isAdmin: newControlRecord.isAdmin, isOwned: newControlRecord.isOwned() });
    this.setLoaded(false);
    if (this.subscriptionManager.hasListeners(ListenerType.ControlRecordChanged)) {
      this.subscriptionManager.fireListenersSync(
        ListenerType.ControlRecordChanged,
        this.controlRecord
      );
    }
  };

  private onSessionProgressStateChanged = (
    sessionProgressState: ISessionProgressState
  ) => {
    if (this.controlRecord === null) return;

    this.controlRecord.mergeSessionProgressStateChange(sessionProgressState);

    if (this.subscriptionManager.hasListeners(ListenerType.ControlRecordChanged)) {
      this.subscriptionManager.fireListenersSync(
        ListenerType.ControlRecordChanged,
        this.controlRecord
      );
    }
  };

  private onOwnershipChanged = (ownershipRecord: IOwnershipControl) => {
    if (this.controlRecord === null) return;

    this.controlRecord.mergeOwnershipRecord(ownershipRecord);
    this.ensureOwnerSettingsSubscription();
    this.setLoaded(false);

    if (this.subscriptionManager.hasListeners(ListenerType.ControlRecordChanged)) {
      this.subscriptionManager.fireListenersSync(
        ListenerType.ControlRecordChanged,
        this.controlRecord
      );
    }
  };

  protected onSettingsChanged(settings: ISessionSettings): void {
    this.settings = settings;
    this.awaitingSettings = false;
    loadPerf(`Settings.onSettingsChanged (awaitingSettings cleared)`, { bytes: JSON.stringify(settings ?? {}).length });
    this.setLoaded(true);
  }

  public getUnavailableDays = (): EventDay[] => {
    return [];
  };

  public getHolidays = (): Holiday[] => {
    return [];
  };

  private ownerSettingsChangedCallback = (
    ownerSettings: ISessionSettings
  ): void => {
    this.ownerSettings = ownerSettings;
    this.awaitingOwnerSettings = false;
    loadPerf(`Settings.ownerSettingsChanged (awaitingOwnerSettings cleared)`, { bytes: JSON.stringify(ownerSettings ?? {}).length });
    this.setLoaded(true);
  };

  protected updateCollection<T extends BaseItem>(
    collection: ISettingsItem[],
    item: ISettingsItem,
    updateSettings: (items: ISettingsItem[]) => void,
    createInstance: (item: ISettingsItem) => T
  ): boolean {
    const match = collection.findIndex((i) => i.key === item.key);
    const newInstance = createInstance(item);
    if (match === -1 || !item.key) {
      item.key = uuid(); // update the input item with the new key so that the same key can be used on all other settings
      newInstance.key = item.key;
      collection.push(newInstance);
    } else {
      collection[match] = newInstance;
    }
    updateSettings(collection);
    this.fireListeners();
    return true;
  }

  public getControl = (): ControlRecord => {
    return this.controlRecord;
  };

  public isSettingsOwner = (): boolean => {
    return this.getControl().isSettingsOwner(
      this.cookies,
      this.isSystemLoggedOn()
    );
  };

  public getSystemSettings = (): ISessionSettings => {
    return this.systemSettings ?? defaultSettings;
  };

  public isAdminLocked = (): boolean => {
    return (
      this.settings.adminLockPassword !== undefined &&
      this.settings.adminLockPassword.length > 0
    );
  };

  public adminUnlock = (pwd: string, passphrase: string) => {
    if (!this.settings.adminLockPassword) return true;
    const decrypted = decryptWithAES(
      this.settings.adminLockPassword,
      passphrase
    );
    return decrypted === pwd;
  };

  public adminLock = (pwd: string, passphrase: string) => {
    if (!pwd || !passphrase) return true;
    const encrypted = encryptWithAES(pwd, passphrase);
    this.settings.adminLockPassword = encrypted;
    void this.saveGeneralSettings(this.settings, undefined, false, encrypted);
  };

  public adminRemoveLock = () => {
    this.settings.adminLockPassword = undefined;
    void this.saveGeneralSettings(this.settings, undefined, false, null);
  };

  public getSessionSettings = (): ISessionSettings => this.settings;

  public saveSiteEffectsSettings = async (
    siteEffectsSettings: ISiteEffectsSettings
  ): Promise<boolean> => {
    this.settings.siteEffectsSettings = cloneObject(siteEffectsSettings);
    return this.updateSettings(this.settings);
  };

  public getTask = (key: string): ITaskItem | undefined =>
    cloneObject(
      this.getTasks((i) => i.key === key, false, false)[0]
      ?? this.getTasks((i) => i.key === key, true, true)[0]
    );
  public getPosition = (key: string): ISettingsItem | undefined =>
    cloneObject(
      this.getPositions((i) => i.key === key, false, false)[0]
      ?? this.getPositions((i) => i.key === key, true, true)[0]
    );
  public getEvent = (key: string): IEventItem | undefined =>
    cloneObject(
      this.getEvents((i) => i.key === key, false, false)[0]
      ?? this.getEvents((i) => i.key === key, true, true)[0]
    );
  public getPermission = (key: string): IPermissionItem | undefined =>
    cloneObject(
      this.getPermissions((i) => i.key === key, false, false)[0]
      ?? this.getPermissions((i) => i.key === key, true, true)[0]
    );
  public getPenalty = (key: string): IPenaltyItem | undefined =>
    cloneObject(
      this.getPenalties((i) => i.key === key, false, false)[0]
      ?? this.getPenalties((i) => i.key === key, true, true)[0]
    );
  public getGeneralSettings = (): IGeneralSettings => this.settings;
  public getTaskSettings = (): ITaskSettings =>
    this.settings.creditSettings ??
    this.getSystemSettings().creditSettings ??
    {};
  public getPenaltySettings = (): IPenaltySettings =>
    this.settings.penaltySettings ??
    this.getSystemSettings().penaltySettings ??
    {};
  public getPositionSettings = (): IPositionSettings =>
    this.settings.positionSettings ??
    this.getSystemSettings().positionSettings ??
    {};
  public getPermissionSettings = (): IPermissionSettings =>
    this.settings.permissionSettings ??
    this.getSystemSettings().permissionSettings ??
    {};
  public getEventSettings = (): IEventSettings =>
    this.settings.eventsSettings ??
    this.getSystemSettings().eventsSettings ??
    {};

  private filterSettingItems<T extends BaseItem>(
    items: T[] = [],
    filter?: (i: T) => boolean,
    includeNew?: boolean,
    adminMode?: boolean
  ): T[] {
    return items
      .filter(i =>
        adminMode ||
        i.createdBy?.includes(this.getControl().userid) ||
        (!i.isDeleted &&
          (!i.requiresValidation || i.createdBy === this.getControl().userid) &&
          (this.isSystemSettings() || includeNew || !i.isNew)
        )
      )
      .filter(i => !filter || filter(i))
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }

  public getPositions = (
    filter?: (i: ISettingsItem) => boolean,
    includeNew?: boolean,
    adminMode?: boolean
  ): PositionItem[] => {
    return this.filterSettingItems(
      this.settings?.positionSettings?.positions,
      filter,
      includeNew,
      adminMode
    ).map((t) => new PositionItem(t));
  };

  public getEvents = (
    filter?: (i: IEventItem) => boolean,
    includeNew?: boolean,
    adminMode?: boolean
  ): EventItem[] => {
    return this.filterSettingItems(
      this.settings?.eventsSettings?.events,
      filter,
      includeNew,
      adminMode
    ).map((t) => new EventItem(t));
  };

  public getPermissions = (
    filter?: (i: IPermissionItem) => boolean,
    includeNew?: boolean,
    adminMode?: boolean
  ): PermissionItem[] => {
    return this.filterSettingItems(
      this.settings?.permissionSettings?.permissions,
      filter,
      includeNew,
      adminMode
    ).map((t) => new PermissionItem(t));
  };

  public getPenalties = (
    filter?: (i: IPenaltyItem) => boolean,
    includeNew?: boolean,
    adminMode?: boolean
  ): PenaltyItem[] => {
    return this.filterSettingItems(
      this.settings?.penaltySettings?.penalties,
      filter,
      includeNew,
      adminMode
    ).map((t) => new PenaltyItem(t));
  };


  public getTasks(
    filter?: (i: ITaskItem) => boolean,
    includeNew?: boolean,
    adminMode?: boolean
  ): TaskItem[] {
    const results = this.filterSettingItems(
      this.settings?.creditSettings?.credits,
      filter,
      includeNew,
      adminMode
    ).map((t) => new TaskItem(t));
    return results;
  }

  public getOutfits = () => {
    const tasks = this.getTasks();
    const newOutfits = tasks.filter(t => !t.isItem
      && t.categories
      && t.categories.find((c) => isOutfitCategory(c))
      && this.checkItemIsOwned(t));
    return newOutfits;
  }

  public getItemsOfType = (itemType: ItemTypes): ISettingsItem[] => {
    const allowEditorFallback = this.canEditSettings();
    const editorFilter = (i: ISettingsItem) => !i.isDeleted && !i.hidden;

    switch (itemType) {
      case ItemTypes.event:
        return this.getEvents(
          allowEditorFallback ? editorFilter as (i: IEventItem) => boolean : (i) => this.canAddTask(i).isOk,
          allowEditorFallback,
          allowEditorFallback
        );
      case ItemTypes.penalty:
        return this.getPenalties(
          allowEditorFallback ? editorFilter as (i: IPenaltyItem) => boolean : (i) => this.canAddTask(i).isOk,
          allowEditorFallback,
          allowEditorFallback
        );
      case ItemTypes.permission:
        return this.getPermissions(
          allowEditorFallback ? editorFilter as (i: IPermissionItem) => boolean : (i) => this.canAddTask(i).isOk,
          allowEditorFallback,
          allowEditorFallback
        );
      case ItemTypes.position:
        return this.getPositions(
          allowEditorFallback ? editorFilter : (i) => this.canAddTask(i).isOk,
          allowEditorFallback,
          allowEditorFallback
        );
      case ItemTypes.task:
        return this.getTasks(
          allowEditorFallback ? editorFilter as (i: ITaskItem) => boolean : (i) => this.canAddTask(i).isOk,
          allowEditorFallback,
          allowEditorFallback
        );
    }
    return [];
  };

  private memberOwnerProfileSummaryChangedCallback = (ownerProfile: string): void => {
    if (ownerProfile) {
      this.memberOwnerProfileSummary = decrypt(
        ownerProfile as unknown as string,
        {}
      ) as IOwnerProfileSession;
    }
    this.fireListeners();
  };

  private ownerProfileSummaryChangedCallback = (ownerProfile: string): void => {
    if (ownerProfile) {
      this.ownerProfileSummary = decrypt(
        ownerProfile as unknown as string,
        {}
      ) as IOwnerProfileSession;
    }
    this.fireListeners();
  };

  private ownerProfileSettingsChangedCallback = (
    ownerProfileSettings: ISessionSettings
  ): void => {
    if (ownerProfileSettings) {
      this.memberOwnerProfileSettings = mergeSettings(
        ownerProfileSettings,
        undefined,
        this.systemSettings,
        false
      );
    }
    this.fireListeners();
  };

  public getMemberOwnerProfileSummary = ():
    | IOwnerProfileSession
    | undefined => {
    return this.memberOwnerProfileSummary;
  };

  public getOwnerProfileSummary = (): IOwnerProfileSession | undefined => {
    return this.ownerProfileSummary;
  };

  /* called from edit settings screen for owners and step 1 of the new wizard */
  /* this needs to be re-called whenever settings are changed on the owner session, as it will impact the score */
  public saveOwnerProfile = async (
    profile: IOwnerProfileSession
  ): Promise<void> => {
    profile.userid = getOwnerUserId(profile.userid);
    profile.score = this.getDemandingScore(profile, this.settings);
    profile.hintCategories = this.getHintProfileCategories(
      profile.categories,
      this.settings
    );
    let diffs: Record<string, any> | undefined = undefined;
    if (this.memberOwnerProfileSummary)
      diffs = diffObjects(this.memberOwnerProfileSummary, profile);
    this.memberOwnerProfileSummary = profile;
    await this.database.saveOwnerProfile(profile);
    this.auditService.auditOwnerItemAction("Owner Profile Updated", diffs);
  };

  /* called when the wizard finishes adding or editing a system owner profile */
  public saveOwnerProfileSettings = (
    profile: IOwnerProfileSession,
    profileSettings: ISessionSettings
  ): void => {
    if (!profile) return;
    profile.userid = getOwnerUserId(profile.userid);
    profile.score = this.getDemandingScore(profile, profileSettings);
    profile.hintCategories = this.getHintProfileCategories(
      profile.categories,
      profileSettings
    );
    void this.database.addOwnerProfile(profile, profileSettings);
    this.showMessage({
      message: `Owner Profile ${profile.name} updated`,
      persist: true,
      notificationType: NotificationType.ProfileInfo,
    });
    this.auditService.auditUserAction(
      `Owner Profile ${profile.name} updated`,
      AuditActionTypes.Template
    );
  };

  protected isScheduleItemAvailableDay = (p: IScheduledItem, day: EventDay) => {
    if (p.availability === undefined) return true; // treat as daily when not availble
    return (
      p.availability === AvailabilityType.daily ||
      (p.availability === AvailabilityType.weekdays &&
        !isWeekendDay(day, this.peekWeekendDays())) ||
      (p.availability === AvailabilityType.weekends &&
        isWeekendDay(day, this.peekWeekendDays())) ||
      (p.availability === AvailabilityType.specific &&
        p.specificDays &&
        p.specificDays.includes(day)) ||
      p.availability === AvailabilityType.random
    );
  };

  public auditUserAction = (
    message: string,
    actionType: AuditActionTypes
  ): void => {
    this.auditService.auditUserAction(message, actionType);
  }

  public canPerformAllTasksForDate(date: Date): boolean {
    const day = date.getDay();
    return this.canPerformAllTasksForDay(day);
  }

  public getCheckoutPermission = (): IPermissionItem | undefined => {
    return this.getPermissions((i) => i.eventType === EventType.checkout)[0];
  };

  public getItemCategories = (item: ISettingsItem): string[] => {
    return this.categoryHelper.getCategoriesForItem(
      item,
      (key) => this.getTask(key),
      (key) => this.getPenalty(key),
      (key) => this.getEvent(key),
      (key) => this.getPermission(key)
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public canPerformAllTasksForDay(_day: JavascriptDay): boolean {
    return true;
  }

  public canPerformSomeTasksForToday(): boolean {
    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public canPerformSomeTasksForDate(_date: Date): boolean {
    return true;
  }

  public getTaskSchedule(): ITaskBasketInfo {
    return {};
  }

  public canAddCategory(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _item: ICategoryItem,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _taskSchedule: (ITaskTimerInfo | IPenaltyTimerInfo)[]
  ): boolean {
    return true;
  }

  protected getCanAddTaskReason(
    basketItem: ISettingsItem,
    pendingItems: IPendingItem[],
    date?: Date,
    bypassDateChecks?: boolean
  ): string | undefined {
    if (!basketItem) {
      return "Unable to find the item to add";
    }

    const item = this.getCurrentItem(basketItem);
    if (!item) {
      return "Unable to find the item to add";
    }

    let isPerm = false;
    if (item.itemType === ItemTypes.task) {
      isPerm = (item as ITaskItem).preSelect === PreselectType.permanent;
    }
    if (
      !date &&
      !bypassDateChecks &&
      !isPerm &&
      !this.getControl().isOwnerProfile() &&
      !this.canPerformSomeTasksForToday()
    )
      return "Unable to perform tasks today";

    if (
      item.itemType === ItemTypes.event &&
      (item as IEventItem).eventType === EventType.startOfDay
    )
      return undefined;

    const alias = `${(item as ITaskItem).isItem
      ? "Item"
      : getItemTypeDisplayName(item.itemType)
      } ${item.name}`;

    if (item.requiresValidation) {
      return `The ${alias} has yet to be approved by system`;
    }

    if (item.isNew && !this.isSystemLoggedOn()) {
      return `The ${(item as ITaskItem).isItem
        ? "Item"
        : getItemTypeDisplayName(item.itemType)
        } has yet to be approved by ${this.controlRecord.isOwned() ? "Owner" : "you"
        }`;
    }

    if (
      item.itemType === ItemTypes.task ||
      item.itemType === ItemTypes.penalty
    ) {
      if (
        (item as ITaskItem | IPenaltyItem).requiresOwner &&
        this.controlRecord.isOwnedBySelf() &&
        !this.getControl().isOwnerProfile()
      )
        return `The ${alias} required a real owner`;
    }
    if (item.hidden) {
      return `The ${alias} is hidden`;
    }

    if (item.isDeleted) {
      return `The ${alias} is deleted`;
    }

    if (
      (item as IOwnerRequired).requiresOwner &&
      this.getControl().isOwnedBySelf() &&
      !this.getControl().isOwnerProfile()
    ) {
      return `The ${alias} requires member to be owned to run`;
    }

    if (
      item.itemType === ItemTypes.task ||
      item.itemType === ItemTypes.penalty
    ) {
      if (
        (item as IItemSupport).isItem &&
        !this.getControl().isOwnerProfile() &&
        this.getOwnedItems()[item.key] === undefined
      )
        return `The ${alias} is not owned`;
      else if (
        !this.checkItemIsOwned(item) &&
        !this.getControl().isOwnerProfile()
      )
        return `${alias} uses Items not owned`;
    }

    const taskCat = (item as ITaskItem).selectCategoryItem;
    if (
      item.itemType === ItemTypes.task &&
      taskCat &&
      !bypassDateChecks &&
      !this.canAddTaskForCategory(taskCat, item)
    ) {
      return `There are no valid tasks available for the category ${taskCat} to be selected`;
    }
    return this.getTaskScheduleReason(
      item,
      pendingItems,
      bypassDateChecks,
      date,
      this.getTaskSchedule()
    );
  }

  public getTaskScheduleReason(
    item: ISettingsItem,
    pendingItems: IPendingItem[],
    bypassDateChecks: boolean | undefined,
    date: Date | undefined,
    scheduledItems: ITaskBasketInfo
  ): string | undefined {
    const alias = `${(item as ITaskItem).isItem
      ? "Item"
      : getItemTypeDisplayName(item.itemType)
      } ${item.name}`;
    if (
      item.itemType === ItemTypes.task ||
      item.itemType === ItemTypes.penalty
    ) {
      const catItem = item as ITaskItem | IPenaltyItem;

      if (
        this.controlRecord.missingCategories &&
        !this.getControl().isOwnerProfile() &&
        this.controlRecord.missingCategories.filter &&
        this.controlRecord.missingCategories.filter(
          (c) => catItem.categories && catItem.categories.includes(c)
        ).length > 0
      ) {
        return `The ${alias} uses a categories ${catItem.categories
          ?.filter((c) => this.controlRecord.missingCategories?.includes(c))
          ?.join()} which is a limit`; // this item uses a category which is a limit so we can't add it
      }

      if (
        !bypassDateChecks &&
        !this.getControl().isOwnerProfile() &&
        (!date || isToday(date))
      ) {
        if (scheduledItems[item.key] && !scheduledItems[item.key].isComplete) {
          return `The ${alias} is already in task list`;
        }

        if (
          Object.values(scheduledItems).find(
            (i) => i.selectedItems && i.selectedItems[item.key]
          )
        ) {
          return `The ${alias} is already in used by another task in the list`;
        }

        if (
          !this.canAddCategory(catItem, Object.values(this.getTaskSchedule()))
        )
          return `An ${alias} is already added which has a category set to be only selected once per day`;

        if (
          pendingItems.find(
            (i) =>
              i.item &&
              i.item.key === item.key &&
              (i.fireImmediate ||
                (i.date && isBefore(new Date(i.date), endOfToday()))) &&
              i.updateType === PendingUpdateType.add
          )
        ) {
          return `The ${alias} is already scheduled today`;
        }
        if (!this.canPerformSomeTasksForToday()) {
          return `The ${alias} cannot be scheduled today`;
        }
        if (
          !this.itemAvailableForDay(
            item,
            convertToEventDayFromDate(new Date().getDay()),
            false,
            false
          )
        ) {
          return `The ${alias} cannot be scheduled on a busy day`;
        }
      } else if (!bypassDateChecks && date) {
        if (!this.canPerformSomeTasksForDate(date)) {
          return `The ${alias} cannot be scheduled for date as diary shows unavailable`;
        }
        if (
          !this.itemAvailableForDay(
            item,
            convertToEventDayFromDate(date.getDay()),
            false,
            false
          )
        ) {
          return `The ${alias} cannot be scheduled on a busy day`;
        }
      }
    }
  }

  public getPreviewTaskScheduleReason(
    item: ISettingsItem,
    pendingItems: IPendingItem[],
    date: Date,
    scheduledItems: (ITaskTimerInfo | IPenaltyTimerInfo)[]
  ): string | undefined {
    const alias = `${(item as ITaskItem).isItem
      ? "Item"
      : getItemTypeDisplayName(item.itemType)
      } ${item.name}`;
    if (
      item.itemType === ItemTypes.task ||
      item.itemType === ItemTypes.penalty
    ) {
      const catItem = item as ITaskItem | IPenaltyItem;

      if (
        this.controlRecord.missingCategories &&
        !this.getControl().isOwnerProfile() &&
        this.controlRecord.missingCategories.filter &&
        this.controlRecord.missingCategories.filter(
          (c) => catItem.categories && catItem.categories.includes(c)
        ).length > 0
      ) {
        return `The ${alias} uses a categories ${catItem.categories
          ?.filter((c) => this.controlRecord.missingCategories?.includes(c))
          .join()} which is a limit`; // this item uses a category which is a limit so we can't add it
      }

      if (scheduledItems && scheduledItems.find((i) => i.key === item.key)) {
        return `The ${alias} is already in task list`;
      }

      if (
        scheduledItems &&
        scheduledItems.find((i) => i.selectedItems && i.selectedItems[item.key])
      ) {
        return `The ${alias} is already in used by another task in the list`;
      }

      if (scheduledItems && !this.canAddCategory(catItem, scheduledItems))
        return `An ${alias} is already added which has a category set to be only selected once per day`;

      if (
        pendingItems.find(
          (i) =>
            i.item &&
            i.item.key === item.key &&
            (i.fireImmediate || (i.date && isTomorrow(new Date(i.date)))) &&
            i.updateType === PendingUpdateType.add
        )
      ) {
        return `The ${alias} is already scheduled tomorrow`;
      }
      if (!this.canPerformSomeTasksForDate(date)) {
        return `The ${alias} cannot be scheduled for date as diary shows unavailable`;
      }
      if (
        !this.itemAvailableForDay(
          item,
          convertToEventDayFromDate(date.getDay()),
          false,
          false
        )
      ) {
        return `The ${alias} cannot be scheduled on a busy day`;
      }
    }
  }

  public canAddTask(
    basketItem: ISettingsItem,
    date?: Date,
    bypassDateChecks?: boolean
  ): { isOk: boolean; reason: string | undefined } {
    const reason = this.getCanAddTaskReason(
      basketItem,
      [],
      date,
      bypassDateChecks
    );
    return { isOk: reason === undefined, reason };
  }

  public canAddTaskForCategory = (
    selectedCategory: string,
    triggerItem: ISettingsItem
  ): boolean => {
    if (!selectedCategory) return false;
    const tasks = this.getTasks(
      (c) =>
        isAvailableItem(c) &&
        c.key !== triggerItem.key &&
        c.categories !== undefined &&
        !c.selectCategoryItem
    );

    const allowed = tasks.filter(
      (t) =>
        t.categories &&
        t.categories.filter((cat) => cat === selectedCategory)?.length > 0 &&
        this.canAddTask(t).isOk
    );

    return allowed.length > 0;
  };

  public getCategoriesForEvent = (eventKey: string): string[] => {
    return this.categoryHelper.getCategoriesForEvent(
      this.getEvent(eventKey),
      (key) => this.getTask(key)
    );
  };

  public getCategoriesForPermission = (permissionKey: string): string[] => {
    return this.categoryHelper.getCategoriesForPermission(
      this.getPermission(permissionKey),
      (key) => this.getTask(key),
      (key) => this.getPenalty(key)
    );
  };

  public getCategoriesForPenalty = (penaltyKey: string): string[] => {
    return this.categoryHelper.getCategoriesForPenalty(
      this.getPenalty(penaltyKey),
      (key) => this.getTask(key)
    );
  };

  public getCategoriesForTask = (taskKey: string): string[] => {
    return this.categoryHelper.getCategoriesForTask(
      this.getTask(taskKey),
      (key) => this.getTask(key)
    );
  };

  private getSelectedCategories = (
    credits: ITaskItem[] | undefined
  ): string[] => {
    return this.categoryHelper.getSelectedCategories(credits);
  };

  public getHintProfileCategories = (
    favCategories: string[] | undefined,
    settings: ISessionSettings
  ): string[] => {
    return this.categoryHelper.getHintProfileCategories(favCategories, settings);
  };

  public getMissingProfileCategories = (favCategories: string[]): string[] => {
    return this.categoryHelper.getMissingProfileCategories(
      favCategories,
      this.getCategories(false)
    );
  };

  public updateWithCategories = (
    categories: string[] | undefined,
    omitCategories: string[] | undefined
  ): void => {
    this.categoryHelper.updateWithCategories(
      this.getTasks(),
      this.getEvents(),
      this.getPermissions(),
      this.getPenalties(),
      categories,
      {
        forTask: (key) => this.getCategoriesForTask(key),
        forEvent: (key) => this.getCategoriesForEvent(key),
        forPermission: (key) => this.getCategoriesForPermission(key),
        forPenalty: (key) => this.getCategoriesForPenalty(key),
      }
    );
    this.disableLimits(this.settings, omitCategories);
  };

  public disableLimits = (
    settings: ISessionSettings,
    omitCategories: string[] | undefined
  ): void => {
    this.categoryHelper.disableLimits(
      settings,
      omitCategories,
      {
        forTask: (key) => this.getCategoriesForTask(key),
        forEvent: (key) => this.getCategoriesForEvent(key),
        forPermission: (key) => this.getCategoriesForPermission(key),
        forPenalty: (key) => this.getCategoriesForPenalty(key),
      }
    );
  };

  private getDemandingScore = (
    profile: IOwnerProfileSession,
    settings: ISessionSettings
  ): number => {
    let score = 0;
    const profileSettings = settings;
    if (!profile || !profileSettings) return score;
    // normal is medium
    // strict is high
    if (profile.strictness === Strictness.normal) score += 5;
    else if (profile.strictness === Strictness.harsh) score += 20;

    if (profile.lockOnSelection) score += 5;
    if (profile.lockOnSelection && profile.lockType === LockTypes.duration) {
      if (profile.lockCountType === LockCountType.days) score += 5;
      else if (profile.lockCountType === LockCountType.weeks) score += 20;
    } else if (profile.lockOnSelection && profile.lockType === LockTypes.target)
      score += 15; // harder as can go up and down

    if (!profileSettings.eventsSettings.disableEvents) {
      const visibleEvents = profileSettings.eventsSettings.events.filter((e) =>
        isAvailableItem(e)
      );
      // more than 5 events medium
      // more than 8 events is high
      if (visibleEvents.length > 7) score += 15;
      else if (visibleEvents.length > 4) score += 5;
    }

    // 50% of tasks is medium
    // 75% of tasks is high

    if (!profileSettings.creditSettings.disableSystemTasks) {
      if (profileSettings.creditSettings.dailyMandatoryCredits > 5)
        score += 20;
      else if (profileSettings.creditSettings.dailyMandatoryCredits > 1)
        score += 5;

      const visibleTasks = profileSettings.creditSettings.credits.filter((e) =>
        isAvailableItem(e)
      );
      if (
        visibleTasks.length / profileSettings.creditSettings.credits.length >
        0.74
      )
        score += 15;
      else if (
        visibleTasks.length / profileSettings.creditSettings.credits.length >
        0.49
      )
        score += 5;
    }


    // 50% of punishments sets is medium
    // 75% of punishments is high
    const visiblePunishments = profileSettings.penaltySettings.penalties.filter(
      (e) => isAvailableItem(e)
    );
    if (
      visiblePunishments.length /
      profileSettings.penaltySettings.penalties.length >
      0.74
    )
      score += 15;
    else if (
      visiblePunishments.length /
      profileSettings.penaltySettings.penalties.length >
      0.49
    )
      score += 5;

    return Math.round((score / 130) * 100);
  };

  public getOwnerSettings = () => this.ownerSettings;

  public updateOwnerSettingsItem(editItem: ISettingsItem): void {
    if (this.memberOwnerProfileSummary) {
      // this is a new item
      if (this.getControl().userid === this.cookies.userid) {
        if (this.memberOwnerProfileSettings) {
          switch (editItem.itemType) {
            case ItemTypes.task: {
              this.updateCollection(
                this.memberOwnerProfileSettings.creditSettings.credits,
                editItem,
                (items) =>
                  (this.settings.creditSettings.credits = items as ITaskItem[]),
                (item) => new TaskItem(item as ITaskItem)
              );
              break;
            }
            case ItemTypes.position: {
              this.updateCollection(
                this.memberOwnerProfileSettings.positionSettings.positions,
                editItem,
                (items) =>
                (this.settings.positionSettings.positions =
                  items as ISettingsItem[]),
                (item) => new PositionItem(item as ISettingsItem)
              );
              break;
            }
            case ItemTypes.permission: {
              this.updateCollection(
                this.memberOwnerProfileSettings.permissionSettings.permissions,
                editItem,
                (items) =>
                (this.settings.permissionSettings.permissions =
                  items as IPermissionItem[]),
                (item) => new PermissionItem(item as IPermissionItem)
              );
              break;
            }
            case ItemTypes.event: {
              this.updateCollection(
                this.memberOwnerProfileSettings.eventsSettings.events,
                editItem,
                (items) =>
                  (this.settings.eventsSettings.events = items as IEventItem[]),
                (item) => new EventItem(item as IEventItem)
              );
              break;
            }
            case ItemTypes.penalty: {
              this.updateCollection(
                this.memberOwnerProfileSettings.penaltySettings.penalties,
                editItem,
                (items) =>
                (this.settings.penaltySettings.penalties =
                  items as IPenaltyItem[]),
                (item) => new PenaltyItem(item as IPenaltyItem)
              );
              break;
            }
          }
          this.saveOwnerProfileSettings(
            this.memberOwnerProfileSummary,
            this.memberOwnerProfileSettings
          );
        }
      }
    }
  }

  public updateSettingsItems(
    items: ISettingsItem[],
    addToOwner?: boolean,
    bypassSettingOwnerCheck?: boolean
  ): boolean {
    let isOk = true;
    items.forEach((item) => {
      isOk =
        isOk &&
        this.updateSettingsItem(
          item,
          addToOwner,
          bypassSettingOwnerCheck,
          true
        );
    });
    if (isOk) void this.updateSettings(this.settings);
    return isOk;
  }

  /**
   * Attempts to add a new item directly to the owner profile when the current
   * user is both the settings owner and has an active owner-profile.
   * Returns true if the item was added (caller should not also add it locally).
   */
  private addItemToOwnerProfile(editItem: ISettingsItem): boolean {
    if (!this.memberOwnerProfileSummary) return false;
    if (this.getControl().userid !== this.cookies.userid) return false;
    if (!this.memberOwnerProfileSettings) return false;
    editItem.isNew = false;
    editItem.requiresValidation = false;
    switch (editItem.itemType) {
      case ItemTypes.task: {
        this.memberOwnerProfileSettings.creditSettings.credits.push(
          editItem as ITaskItem
        );
        break;
      }
      case ItemTypes.position: {
        this.memberOwnerProfileSettings.positionSettings.positions.push(
          editItem as ISettingsItem
        );
        break;
      }
      case ItemTypes.permission: {
        this.memberOwnerProfileSettings.permissionSettings.permissions.push(
          editItem as IPermissionItem
        );
        break;
      }
      case ItemTypes.event: {
        this.memberOwnerProfileSettings.eventsSettings.events.push(
          editItem as IEventItem
        );
        break;
      }
      case ItemTypes.penalty: {
        this.memberOwnerProfileSettings.penaltySettings.penalties.push(
          editItem as IPenaltyItem
        );
        break;
      }
    }
    this.saveOwnerProfileSettings(
      this.memberOwnerProfileSummary,
      this.memberOwnerProfileSettings
    );
    return true;
  }

  public updateSettingsItem(
    editItem: ISettingsItem,
    addToOwner?: boolean,
    bypassSettingOwnerCheck?: boolean,
    suppressAutoSave?: boolean
  ): boolean {
    const isNew = !editItem.key;
    if (!editItem.key) editItem.key = uuid();
    const currentItem = this.getCurrentItem(editItem) as ISettingsItem | undefined;
    const currentItemSnapshot = currentItem ? cloneObject(currentItem) as ISettingsItem : undefined;
    if (currentItemSnapshot) editItem = { ...currentItemSnapshot, ...editItem };
    editItem.isNew = editItem.isNew || (this.isSystemSettings() && isNew);
    editItem.isSystem = this.isSystemSettings();
    editItem.isOwner = false;
    editItem.isCustom = true;
    editItem.requiresValidation =
      editItem.requiresValidation ||
      (editItem.isNew && !this.isSystemSettings());
    if (isNew && !editItem.createdBy) {
      editItem.createdBy = this.cookies.userid;
    }
    if (isNew || !editItem.createdDate) {
      editItem.createdDate = new Date();
    }
    let added = false;
    if (isNew && addToOwner && this.addItemToOwnerProfile(editItem)) {
      added = true; // adding to the owner will add to the user
    }

    if (!added) {

      const diffs = diffObjects(currentItemSnapshot as ISettingsItem, cloneObject(editItem) as ISettingsItem);
      if (Object.keys(diffs).length === 0) {
        console.log("Skipping saving as no changes made");
        return true; // no changes to save
      }

      switch (editItem.itemType) {
        case ItemTypes.task: {
          return this.taskEditItem(
            editItem as ITaskItem,
            bypassSettingOwnerCheck,
            suppressAutoSave
          );
        }
        case ItemTypes.position: {
          return this.positionEditItem(
            editItem,
            bypassSettingOwnerCheck,
            suppressAutoSave
          );
        }
        case ItemTypes.permission: {
          return this.permissionEditItem(
            editItem as IPermissionItem,
            bypassSettingOwnerCheck,
            suppressAutoSave
          );
        }
        case ItemTypes.event: {
          return this.eventEditItem(
            editItem as IEventItem,
            bypassSettingOwnerCheck,
            suppressAutoSave
          );
        }
        case ItemTypes.penalty: {
          return this.penaltyEditItem(
            editItem as IPenaltyItem,
            bypassSettingOwnerCheck,
            suppressAutoSave
          );
        }
      }
    }
    return true;
  }

  public getTaskUnownedItems = (): ITaskItem[] => {
    const items: ITaskItem[] = this.getTaskAreItems();
    return items.filter((i) => !this.getOwnedItems()[i.key]);
  };

  protected getTaskAreItems = (): ITaskItem[] => {
    const items: ITaskItem[] = [];
    this.getTasks(
      (i) => i.isItem !== undefined && i.isItem && isAvailableItem(i)
    ).forEach((c) => {
      items.push(c);
    });
    return items;
  };

  public getTasksWithItems = (): ITaskItem[] => {
    const items: ITaskItem[] = [];
    this.getTasks(
      (c) =>
        (c.isItem !== undefined && c.isItem) ||
        (c.itemsUsed !== undefined && c.itemsUsed.length > 0)
    ).forEach((c) => {
      items.push(c);
    });
    return items;
  };

  public getCategories = (onlyVisible?: boolean): string[] => {
    return this.categoryHelper.getAllCategories(
      this.getTasks(),
      this.getPenalties(),
      this.getPermissions(),
      this.getEvents(),
      onlyVisible
    );
  };

  public getOwnedItems = (): OwnedItemsCollection => {
    return {} as OwnedItemsCollection;
  };

  public get ownedLovenseToys(): LovenseToy[] {
    return [];
  }

  public checkItemIsOwned = (item: IItemSupport): boolean => {
    if (
      !item ||
      (!item.isItem && (!item.itemsUsed || item.itemsUsed?.length === 0))
    )
      return true;
    return false;
  };

  public createBasketItems(
    item: ISettingsItem,
    getIsWeekend: () => boolean,
    scheduleReason: string | undefined,
    date: Date,
    inputOptions?: ItemAdditionOptions,
    sourceItem?: ISettingsItem,
    testRun?: boolean
  ): (TaskBasketItem | PenaltyBasketItem)[] {
    return this.basketService.createBasketItems(
      item,
      getIsWeekend,
      scheduleReason,
      date,
      inputOptions,
      sourceItem,
      testRun
    );
  }

  /* Here we add a task to the credit task list to flag the items to perform as part of the decision for the event. rather than immediately counting down event timers
    we can allow users to start them when they are ready.*/
  public getDoneTask = (itemToAdd: ITaskItem): TaskBasketItem => {
    return this.basketService.getDoneTask(itemToAdd);
  };

  public getAvailableCategoryTasks = (
    selectedCategory: string,
    triggerItem: ISettingsItem,
    date?: Date,
    testRun?: boolean
  ): ITaskItem[] => {
    return this.basketService.getAvailableCategoryTasks(
      selectedCategory,
      triggerItem,
      date,
      testRun
    );
  };

  public getPossibleCategoryTasks = (
    selectedCategory: string,
    triggerItem: ISettingsItem
  ): TaskItem[] => {
    if (!selectedCategory) return [];
    const creditItems = this.getTasks(
      (c) =>
        c.key !== triggerItem.key &&
        c.categories !== undefined &&
        !c.selectCategoryItem &&
        isAvailableItem(c) &&
        c.categories?.filter((cat) => cat === selectedCategory).length > 0
    );
    return creditItems.map((i) => {
      const reason = this.getCanAddTaskReason(i, [], undefined, true);
      if (reason) i.name = `${i.name} - (${reason})`;
      return i;
    });
  };

  public setOwnerName = (newName: string): void => {
    this.updateControlAttributes({ virtualProfileName: newName });
  };

  protected getTaskForCategory = (
    selectedCategory: string,
    triggerItem: ISettingsItem,
    date?: Date,
    testRun?: boolean
  ): ITaskItem | undefined => {
    const matches = this.getAvailableCategoryTasks(
      selectedCategory,
      triggerItem,
      date,
      testRun
    );
    if (matches.length === 0) {
      return;
    }
    const selectedItem = getRandomItemWithCache(
      matches,
      STATE_KEY_HISTORY_CREDITS,
      (t) => t.key,
      this,
      !testRun
    );
    return selectedItem;
  };

  /// gets a random timer tick based on the item passed in using the settings to determine the random range
  public getTaskTimerTicks = (
    item: ITaskItem | IPenaltyItem,
    getIsWeekend: () => boolean
  ): number => {
    return this.basketService.getTaskTimerTicks(item, getIsWeekend);
  };

  // Virtual method for item option selection - can be overridden in StatefulSettings
  public getItemOptionSelectedToday?(
    item: ITaskItem | IPenaltyItem
  ): string | undefined;

  /// we support items, things that use items, and things that use a thing that uses items
  // this caters for the last two cases
  public selectItemsForTask = (
    item: ITaskItem | IPenaltyItem,
    itemAdditionOptions: ItemAdditionOptions | undefined,
    testRun?: boolean
  ): Record<string, string> | undefined => {
    return this.basketService.selectItemsForTask(
      item,
      itemAdditionOptions,
      testRun
    );
  };

  /* virtual method to ensure we can create schedules against stateless settings */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public canPerformAllTasksForEventDay(_day: EventDay) {
    return true;
  }

  /* virtual method to ensure we can create schedules against stateless settings */
  public itemAvailableForDay(
    item: ISettingsItem,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _eventDay: EventDay,
    includeHidden: boolean,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _ignoreAvailability: boolean
  ) {
    if (!item || (!includeHidden && item.hidden) || item.isDeleted)
      return false;
    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public itemAvailableForDayDiary(_item: ISettingsItem, _eventDay: EventDay) {
    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public canPerformSomeTasksForEventDay(_dateDay: EventDay) {
    return true;
  }

  public checkHolidayToday() {
    return false;
  }

  protected findTasks = (
    s: IPermissionItem | IEventItem,
    day: EventDay,
    includeHidden: boolean,
    ignoreAvailability: boolean
  ): ((ITaskItem | IPenaltyItem) & IScheduledInfo)[] => {
    return this.scheduleManager.findTasks(s, day, includeHidden, ignoreAvailability);
  };

  public getTaskDurationString = (
    t: ITaskItem | IPenaltyItem,
    day: EventDay,
    includeType?: boolean
  ): string => {
    if (!t || !t.count || !isTimedItem(t.countType)) return "";
    let mins = getCountMinutes(t.count, t.countType);
    if (day === EventDay.sunday || day === EventDay.saturday) {
      if (this.settings.weekendHandling === WeekendHandling.easy)
        mins = mins / 2;
      if (this.settings.weekendHandling === WeekendHandling.harsh)
        mins = mins * 2;
    }
    const variance = this.getTaskSettings().creditCountVariance;
    const lowerMins = Math.round(mins * (1 - variance));
    const upperMins = Math.round(mins * (1 + variance));
    if (includeType)
      return `(${convertToCountTypeString(
        lowerMins,
        CountType.minutes
      )}-${convertToCountTypeString(upperMins, CountType.minutes)})`;
    return `(${+convertFromMinutesToCountType(lowerMins, t.countType).toFixed(
      1
    )}-${+convertFromMinutesToCountType(upperMins, t.countType).toFixed(1)})`;
  };

  protected describeTask = (
    t: (ITaskItem | IPenaltyItem) & IScheduledInfo,
    day: EventDay,
    from?: ISettingsItem,
    seededCondition?: string,
    seededItemOptions?: Record<string, string>,
    seededItemKeys?: string[],
    seededChoiceName?: string,
  ): (ITaskItem | IPenaltyItem) & IScheduledInfo => {
    return {
      ...t,
      selectedCondition: seededCondition,
      selectedItems: seededItemOptions,
      selectedChoiceName: seededChoiceName,
      selectedItemKeys: seededItemKeys,
      name: `${t.name}${!t.endTriggerEvent && isTimedItem(t.countType)
        ? `\n${this.getTaskDurationString(t, day)} ${CountType[t.countType] as string
        }`
        : ""
        }${t.endTriggerEvent &&
          t.itemType === ItemTypes.task &&
          (t as ITaskItem).preSelect !== PreselectType.permanent
          ? `\nuntil ${this.getEvent(t.endTriggerEvent)?.name}`
          : ""
        }${from
          ? `\n(from ${from.name} ${getItemTypeDisplayName(from.itemType)})`
          : ""
        }`,
      chanceItem:
        from &&
          (from as IScheduledItem).approvalChance &&
          (from as IScheduledItem).approvalChance < 1
          ? true
          : t.chanceItem, // we can tag if this item is optionally applied on the day
    };
  };

  public getState<T>(key: string, defaultVal: T): T {
    return defaultVal;
  }

  public getScheduleInfo = (): ISchedulerTimerInfo[] => [];

  public getPreviewChoicesForItem = (item: ISettingsItem) => {
    const preSelectedState = this.getState<
      Record<string, (TaskBasketItem | PenaltyBasketItem)[]>
    >(PREVIEW_EVENT_TASKS, {});
    return preSelectedState[item.key];
  };
  /// roulette tasks that pick items need to be previewed so that the user knows what they might need
  /// here we determine those that could pick an owned item so we can then select the item to preview
  public shouldReplaceRoutineItemOnPreview = (task: ITaskItem | IPenaltyItem): boolean => {
    return task.itemType === ItemTypes.task
      && (task as ITaskItem).creditTaskType === TaskType.roulette
      && (task as ITaskItem).taskKeys !== undefined
      && (task as ITaskItem).taskKeys?.find((t) => {
        const taskInst = this.getTask(t);
        if (!taskInst) return false;
        return taskInst && TaskItem.IsAnItemToOwn(taskInst); // we only want to preview roulette tasks that are items to own
      }) !== undefined;
  }

  public refreshSettings = async (): Promise<void> => {
    await this.database
      .getUserSettings(getOwnerUserId(this.controlRecord.userid), true)
      .then((userSettings) => {
        if (userSettings)
          this.ownerProfileSettingsChangedCallback(userSettings);
      });
    if (this.controlRecord?.getOwnerType() !== OwnerType.unowned) {
      await this.database
        .getUserSettings(
          getOwnerUserId(this.controlRecord.getOwner().userid),
          true
        )
        .then((settings) => {
          if (settings) this.ownerSettingsChangedCallback(settings);
        });
    }
    await this.database
      .getUserSettings(this.controlRecord.userid, true)
      .then((settings) => {
        if (settings) this.onSettingsChanged(settings);
      });
  };

  // if you can't own it false returned
  // if you not already owner, the ownership taken
  // settings updated with owner also updated to you
  public async updateSettings(
    settings: ISessionSettings,
    systemUpdate?: boolean,
    changedItem?: ISettingsItem
  ): Promise<boolean> {
    if (
      !systemUpdate &&
      !this.controlRecord.canOwnSettings(this.cookies, this.isSystemLoggedOn())
    )
      return false;
    if (
      !systemUpdate &&
      !this.controlRecord.isOwnedBySelf() &&
      !this.controlRecord.isOwnedBy(this.cookies, this.isSystemLoggedOn())
    ) {
      return false;
    }
    this.settings = settings;
    let strippedSettings = settings;
    if (!this.isSystemSettings()) {
      strippedSettings = stripSettings(
        settings,
        this.ownerSettings,
        this.systemSettings,
        false,
        this.controlRecord.userid,
        this.database
      );
    }
    if (strippedSettings.permissionSettings?.disablePermissions !== undefined) {
      this.auditService.auditOwnerItemAction(
        "Settings Write",
        { disablePermissions: strippedSettings.permissionSettings.disablePermissions, ownSettingsChange: this.isLoggedOnUserSession(), userid: this.controlRecord?.userid }
      );
    }
    await this.database.updateSettings(
      this.controlRecord.userid,
      strippedSettings,
      this.isLoggedOnUserSession(),
      changedItem
    );
    // Do not auto-propagate session-level custom items into system defaults.
    // This causes cross-member side effects (items/outfits turning into global
    // pending "new" items) when owners edit a member session.
    this.fireListeners();
    return true;
  }

  public promoteItemToSystem = async (
    itemType: ItemTypes,
    itemKey: string
  ): Promise<boolean> => {
    if (!itemKey) return false;
    if (!this.controlRecord.isOwnedBy(this.cookies, this.isSystemLoggedOn())) {
      return false;
    }

    let item: ISettingsItem | undefined;
    switch (itemType) {
      case ItemTypes.task:
        item = this.getTask(itemKey);
        break;
      case ItemTypes.penalty:
        item = this.getPenalty(itemKey);
        break;
      case ItemTypes.permission:
        item = this.getPermission(itemKey);
        break;
      case ItemTypes.event:
        item = this.getEvent(itemKey);
        break;
      case ItemTypes.position:
        item = this.getPosition(itemKey);
        break;
      default:
        return false;
    }

    if (!item) return false;
    const promoted = await this.database.promoteSettingsItemToSystem(
      this.controlRecord.userid,
      item
    );
    if (promoted) {
      this.auditService.auditOwnerItemAction(
        "Promoted item to system",
        {
          sourceUserId: this.controlRecord.userid,
          itemKey,
          itemType: ItemTypes[itemType],
          itemName: item.name,
        }
      );
    }
    return promoted;
  }

  public getEditGeneralSettings = (): IGeneralSettings => {
    const settings = this.getGeneralSettings();
    return { ...settings };
  };

  public async saveGeneralSettings(
    settings: IGeneralSettings,
    requiredCredit?: number,
    systemUpdate?: boolean,
    adminLockPwd?: string | null
  ): Promise<void> {
    const generalSettings = this.settings;
    if (!systemUpdate) {
      this.auditService.auditOwnerItemAction(
        "Settings Updated",
        diffObjects(generalSettings, settings)
      );
    }
    const {
      backgroundColor,
      applicationTitle,
      showRemainingTimes,
      showBank,
      minReportTime,
      maxReportTime,
      reportWordCount,
      dailyReporting,
      randomiseSettings,
      dailyCost,
      dailyReportingCredit,
      weekendHandling,
      randomVerifications,
      verificationLimit,
      penaltyPointConversionType,
      holidayMode,
      reportStyle,
      holidayEndDate,
      disableDiscordProof,
      disableDiscordTicker,
      disableDiscordShaming,
      hygieneBreaks,
      hygieneIntervalMins,
      hygieneApplyCostPerMin,
      hygieneCostPerMin,
      hygienePenalties,
      hygienePillories,
      hidePendingTasks,
    } = settings;
    generalSettings.backgroundColor = backgroundColor;
    generalSettings.randomiseSettings = randomiseSettings;
    generalSettings.applicationTitle = applicationTitle || "";
    generalSettings.showRemainingTimes = showRemainingTimes;
    generalSettings.showBank = showBank;
    generalSettings.dailyReporting = dailyReporting;
    generalSettings.reportWordCount = reportWordCount;
    generalSettings.maxReportTime = maxReportTime;
    generalSettings.minReportTime = minReportTime;
    generalSettings.dailyCost = dailyCost;
    generalSettings.dailyReportingCredit = dailyReportingCredit;
    generalSettings.weekendHandling = weekendHandling;
    generalSettings.randomVerifications = randomVerifications;
    generalSettings.verificationLimit = verificationLimit;
    generalSettings.penaltyPointConversionType = penaltyPointConversionType;
    generalSettings.hygieneBreaks = hygieneBreaks ?? HygieneBreak.none;
    generalSettings.hygieneIntervalMins = hygieneIntervalMins ?? 0;
    generalSettings.hygieneApplyCostPerMin = hygieneApplyCostPerMin ?? false;
    generalSettings.hygieneCostPerMin = hygieneCostPerMin ?? 0;
    generalSettings.hygienePenalties = hygienePenalties ?? [];
    generalSettings.hygienePillories = hygienePillories ?? [];
    generalSettings.disableDiscordProof = disableDiscordProof;
    generalSettings.disableDiscordTicker = disableDiscordTicker;
    generalSettings.disableDiscordShaming = disableDiscordShaming;
    generalSettings.hidePendingTasks = hidePendingTasks;
    if (holidayEndDate) {
      if (isAfter(holidayEndDate, new Date())) {
        generalSettings.holidayEndDate = holidayEndDate;
        generalSettings.holidayMode = true;
      } else {
        generalSettings.holidayEndDate = undefined;
        generalSettings.holidayMode = false;
      }
    } else {
      generalSettings.holidayMode = holidayMode;
    }
    generalSettings.reportStyle = reportStyle;

    if (requiredCredit) {
      const permission = this.getCheckoutPermission();
      const p = this.getPermissionSettings().permissions.find(
        (p) => p.key === permission?.key
      );
      if (p) {
        p.requiredCredit = requiredCredit;
        this.permissionEditItem(p, systemUpdate);
      }
    }
    if (adminLockPwd !== undefined)
      generalSettings.adminLockPassword = adminLockPwd ?? undefined;
    await this.updateSettings(this.settings, systemUpdate);
    this.fireListeners();
  }

  // permissions

  // generic CRUD helpers

  private editItemGeneric(item: ISettingsItem, bypass?: boolean, suppress?: boolean): boolean {
    const result = this.itemRepository.upsertItem(this.settings, item, bypass, suppress);
    if (result.success && result.requiresSave) void this.updateSettings(this.settings, bypass, item);
    return result.success;
  }

  private async deleteItemGeneric(itemType: ItemTypes, itemKey: string, suppress?: boolean): Promise<void> {
    if (!this.controlRecord.isOwnedBy(this.cookies, this.isSystemLoggedOn())) return;
    const result = this.itemRepository.deleteItem(this.settings, itemType, itemKey, suppress);
    if (result.success && result.requiresSave) await this.updateSettings(this.settings);
  }

  private async resetItemsGeneric(itemType: ItemTypes): Promise<void> {
    this.itemRepository.resetItems(this.settings, defaultSettings, itemType);
    await this.updateSettings(this.settings);
  }

  public permissionEditItem = (
    item: IPermissionItem,
    isSystem?: boolean,
    suppressAutoSave?: boolean
  ): boolean => {
    item.itemType = ItemTypes.permission;
    const result = this.itemRepository.upsertItem(
      this.settings,
      item,
      isSystem,
      suppressAutoSave
    );
    if (result.success && result.requiresSave) {
      void this.updateSettings(this.settings);
    }
    return result.success;
  };

  public permissionDeleteItem = async (
    itemKey: string,
    isSystem?: boolean,
    suppressAutoSave?: boolean
  ): Promise<void> => {
    if (!this.controlRecord.isOwnedBy(this.cookies, this.isSystemLoggedOn()))
      return;

    const permission = this.getPermissions((i) => i.key === itemKey)[0];
    if (!permission) return;
    if (
      (permission.eventType === EventType.checkout ||
        permission.eventType === EventType.startOfDay) &&
      !isSystem
    ) {
      this.showMessage({
        message: "Unable to delete Checkout or Start Of Day permission",
        persist: true,
        error: true,
        notificationType: NotificationType.ErrorInfo,
      });
      return;
    }

    const result = this.itemRepository.deleteItem(
      this.settings,
      ItemTypes.permission,
      itemKey,
      suppressAutoSave
    );
    if (result.success && result.requiresSave) {
      await this.updateSettings(this.settings);
    }
  };

  public permissionsEditSettings = (): IPermissionSettings => {
    const settings = this.getPermissionSettings();
    return { ...settings };
  };

  public permissionsSaveSettings = async (
    settings: IPermissionSettings
  ): Promise<void> => {
    this.auditService.auditOwnerItemAction(
      "Permission Settings Updated",
      diffObjects(this.settings.permissionSettings, settings)
    );
    this.auditService.auditOwnerItemAction(
      "Permission Settings Saving",
      { disablePermissions: settings.disablePermissions, isLoggedOnUser: this.isLoggedOnUserSession(), userid: this.controlRecord?.userid }
    );
    this.settings.permissionSettings = {
      ...settings,
      permissions: this.getPermissionSettings().permissions,
    };
    this.fireListeners();
    await this.updateSettings(this.settings);
  };

  public permissionsResetItems = async (): Promise<void> => {
    await this.resetItemsGeneric(ItemTypes.permission);
  };

  // credits

  public tasksEditSettings = (): ITaskSettings => {
    const settings = this.getTaskSettings();
    return { ...settings };
  };

  public tasksSaveSettings = async (
    settings: ITaskSettings,
    systemUpdate?: boolean
  ): Promise<void> => {
    this.auditService.auditOwnerItemAction(
      "Task Settings Updated",
      diffObjects(this.settings.creditSettings, settings)
    );
    this.settings.creditSettings = {
      ...settings,
      credits: this.getTaskSettings().credits,
    };
    this.fireListeners();
    await this.updateSettings(this.settings, systemUpdate);
  };

  public taskDeleteItem = async (
    itemKey: string,
    suppressAutoSave?: boolean
  ): Promise<void> => {
    await this.deleteItemGeneric(ItemTypes.task, itemKey, suppressAutoSave);
  };

  public taskEditItem = (
    item: ITaskItem,
    bypassSettingOwnerCheck?: boolean,
    suppressAutoSave?: boolean
  ): boolean => {
    item.itemType = ItemTypes.task;
    if (item.isItem) item.itemsUsed = [];
    return this.editItemGeneric(item, bypassSettingOwnerCheck, suppressAutoSave);
  };

  public tasksResetItems = async (): Promise<void> => {
    await this.resetItemsGeneric(ItemTypes.task);
  };

  // positions

  private positionEditItem = (
    item: ISettingsItem,
    bypassSettingOwnerCheck?: boolean,
    suppressAutoSave?: boolean
  ): boolean => {
    item.itemType = ItemTypes.position;
    return this.editItemGeneric(item, bypassSettingOwnerCheck, suppressAutoSave);
  };

  public positionsGetSettings = (): IPositionSettings => {
    const settings = this.getPositionSettings();
    return { ...settings };
  };

  public positionsSaveSettings = async (
    settings: IPositionSettings
  ): Promise<void> => {
    this.auditService.auditOwnerItemAction(
      "Position Settings Updated",
      diffObjects(this.settings.positionSettings, settings)
    );
    this.settings.positionSettings = {
      ...settings,
      positions: this.getPositionSettings().positions,
    };
    this.fireListeners();
    await this.updateSettings(this.settings);
  };

  public positionDeleteItem = async (
    itemKey: string,
    suppressAutoSave?: boolean
  ): Promise<void> => {
    await this.deleteItemGeneric(ItemTypes.position, itemKey, suppressAutoSave);
  };

  public positionsResetItems = async (): Promise<void> => {
    await this.resetItemsGeneric(ItemTypes.position);
  };

  public deleteItem = (item: ISettingsItem, suppressAutoSave?: boolean) => {
    switch (item.itemType) {
      case ItemTypes.event: {
        void this.eventDeleteItem(item.key, true, suppressAutoSave);
        break;
      }
      case ItemTypes.penalty: {
        void this.penaltyDeleteItem(item.key, suppressAutoSave);
        break;
      }
      case ItemTypes.permission: {
        void this.permissionDeleteItem(item.key, true, suppressAutoSave);
        break;
      }
      case ItemTypes.position: {
        void this.positionDeleteItem(item.key, suppressAutoSave);
        break;
      }
      case ItemTypes.task: {
        void this.taskDeleteItem(item.key, suppressAutoSave);
        break;
      }
      default:
        break;
    }
  };

  // events

  public getStartOfDayEvent = (): IEventItem | undefined => {
    if (
      !this.isLoaded() ||
      !this.settings ||
      !this.settings.eventsSettings ||
      !this.mergedSettings
    )
      return undefined;
    const eventSettings = this.getEventSettings();
    if (!eventSettings) return undefined;
    const allEvents = this.getEvents(
      (event) => event.eventType === EventType.startOfDay
    );
    let events: IEventItem[] = [];
    if (allEvents.length > 1)
      events = allEvents.filter((e) => isAvailableItem(e));
    if (events.length === 0) events = allEvents;
    let startOfDayEvent = events[0];
    if (!startOfDayEvent) {
      const now = Date.now();
      if (
        this.pendingStartOfDayEvent &&
        now - this.startOfDayEventCreatedAt <
        Settings.START_OF_DAY_EVENT_CREATION_COOLDOWN_MS
      ) {
        return this.pendingStartOfDayEvent;
      }
      startOfDayEvent = { ...defaultSodEvent } as EventItem;
      events.push(startOfDayEvent);
      if (!this.settings.eventsSettings) {
        const effectiveEventSettings = this.getEventSettings();
        this.settings.eventsSettings = {
          disableEvents: effectiveEventSettings?.disableEvents ?? false,
          permissionDurationVariance:
            effectiveEventSettings?.permissionDurationVariance ?? 0,
          events: [],
        } as IEventSettings;
      }
      this.settings.eventsSettings.events.push(startOfDayEvent);
      this.pendingStartOfDayEvent = startOfDayEvent;
      this.startOfDayEventCreatedAt = now;
      void this.updateSettings(this.settings, true).then(() =>
        this.auditService.auditSettingsItem(
          startOfDayEvent,
          undefined,
          "Start Of Day Event Item Created"
        )
      );
    }
    return startOfDayEvent;
  };

  private eventEditItem = (
    item: IEventItem,
    bypassSettingOwnerCheck?: boolean,
    suppressAutoSave?: boolean
  ): boolean => {
    item.itemType = ItemTypes.event;
    return this.editItemGeneric(item, bypassSettingOwnerCheck, suppressAutoSave);
  };

  public eventDeleteItem = async (
    itemKey: string,
    isSystem?: boolean,
    suppressAutoSave?: boolean
  ): Promise<void> => {
    if (!this.controlRecord.isOwnedBy(this.cookies, this.isSystemLoggedOn()))
      return;

    const event = this.getEvents((i) => i.key === itemKey)[0];

    if (
      event &&
      (event.eventType === EventType.startOfDay ||
        event.eventType === EventType.checkout) &&
      !isSystem
    ) {
      this.showMessage({
        message: "Unable to delete Start Of Day or Checkout events",
        persist: true,
        error: true,
        notificationType: NotificationType.ErrorInfo,
      });
      return;
    }

    const result = this.itemRepository.deleteItem(
      this.settings,
      ItemTypes.event,
      itemKey,
      suppressAutoSave
    );
    if (result.success && result.requiresSave) {
      await this.updateSettings(this.settings);
    }
  };

  public eventsEditSettings = (): IEventSettings => {
    const settings = this.getEventSettings();
    return { ...settings };
  };

  public eventsSaveSettings = async (item: IEventSettings): Promise<void> => {
    this.auditService.auditOwnerItemAction(
      "Event Settings Updated",
      diffObjects(this.settings.eventsSettings, item)
    );
    this.settings.eventsSettings = {
      ...item,
      events: this.getEventSettings().events,
    };
    this.fireListeners();
    await this.updateSettings(this.settings);
  };

  public eventsResetItems = async (): Promise<void> => {
    await this.resetItemsGeneric(ItemTypes.event);
  };

  private penaltyEditItem = (
    item: IPenaltyItem,
    bypassSettingOwnerCheck?: boolean,
    suppressAutoSave?: boolean
  ): boolean => {
    item.itemType = ItemTypes.penalty;
    return this.editItemGeneric(item, bypassSettingOwnerCheck, suppressAutoSave);
  };

  public penaltyDeleteItem = async (
    itemKey: string,
    suppressAutoSave?: boolean
  ): Promise<void> => {
    await this.deleteItemGeneric(ItemTypes.penalty, itemKey, suppressAutoSave);
  };

  public penaltyResetItems = async (): Promise<void> => {
    await this.resetItemsGeneric(ItemTypes.penalty);
  };

  public updateSettingsWithItem = (
    item: ISettingsItem,
    settings: ISessionSettings
  ): void => {
    switch (item.itemType) {
      case ItemTypes.task: {
        updateArray(settings.creditSettings.credits, item);
        break;
      }
      case ItemTypes.penalty: {
        updateArray(settings.penaltySettings.penalties, item);
        break;
      }
      case ItemTypes.position: {
        updateArray(settings.positionSettings.positions, item);
        break;
      }
      case ItemTypes.event: {
        updateArray(settings.eventsSettings.events, item);
        break;
      }
      case ItemTypes.permission: {
        updateArray(settings.permissionSettings.permissions, item);
        break;
      }
      default: {
        return;
      }
    }
  };
  public getCurrentCommunityProfile = (): ControlRecord => {
    return this.getMemberCommunityProfile(this.controlRecord);
  };

  public getPreviewDays(): EventDay[] {
    return [];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public overrideTasksWithOwnedOptions(_tasks: ITaskItem[]): void {
    return;
  }

  public getCurrentItem = (
    item: ISettingsItem
  ):
    | TaskItem
    | PenaltyItem
    | PermissionItem
    | EventItem
    | PositionItem
    | undefined => {
    if (!item) return undefined;
    let key = item.key;
    if (!key) return undefined;
    if (
      (item as ITaskItem).creditTaskType === TaskType.acknowledgement ||
      (item as IPenaltyItem).penaltyType === PenaltyType.acknowledgement
    ) {
      key = key?.split(",")[0];
    }
    switch (item.itemType) {
      case ItemTypes.task: {
        const i = this.getTaskSettings().credits?.find(
          (i) => i.key === key
        ) as ITaskItem;
        this.overrideTasksWithOwnedOptions([i]);
        if (!i) return undefined;
        if (i.imgId && !i.img)
          void this.getDisplayImage(i, "").then((img) => (i.img = img));
        return new TaskItem(i);
      }
      case ItemTypes.permission: {
        const i = this.getPermissionSettings().permissions?.find(
          (i) => i.key === key
        ) as IPermissionItem;
        if (!i) return undefined;
        if (i.imgId && !i.img)
          void this.getDisplayImage(i, "").then((img) => (i.img = img));
        return new PermissionItem(i);
      }
      case ItemTypes.penalty: {
        const i = this.getPenaltySettings().penalties?.find(
          (i) => i.key === key
        ) as IPenaltyItem;
        if (!i) return undefined;
        if (i.imgId && !i.img)
          void this.getDisplayImage(i, "").then((img) => (i.img = img));
        return new PenaltyItem(i);
      }
      case ItemTypes.position: {
        const i = this.getPositionSettings().positions?.find(
          (i) => i.key === key
        ) as ISettingsItem;
        if (!i) return undefined;
        if (i.imgId && !i.img)
          void this.getDisplayImage(i, "").then((img) => (i.img = img));
        return new PositionItem(i);
      }
      case ItemTypes.event: {
        const i = this.getEventSettings().events?.find(
          (i) => i.key === key
        ) as IEventItem;
        if (!i) return undefined;
        if (i.imgId && !i.img)
          void this.getDisplayImage(i, "").then((img) => (i.img = img));
        return new EventItem(i);
      }
    }
    return undefined;
  };





  public canEditSettings = () => {
    return this.controlRecord.isSettingsOwner(
      this.cookies,
      this.isSystemLoggedOn()
    );
  };

  private validateItem = (
    item: ISettingsItem,
    bypassOwnerCheck?: boolean
  ): boolean => {
    if (
      !bypassOwnerCheck &&
      !this.controlRecord.isSettingsOwner(this.cookies, this.isSystemLoggedOn())
    ) {
      this.showMessage({
        message: "Unable to update settings you do not own",
        persist: true,
        error: true,
        notificationType: NotificationType.ErrorInfo,
      });
      return false;
    }
    if (!item.name) {
      this.showMessage({
        message: "Name field must be populated",
        persist: true,
        error: true,
        notificationType: NotificationType.ErrorInfo,
      });
      return false;
    }
    return true;
  };

  public getSessionSummary = (summary?: ISessionSettings): string => {
    if (!summary) summary = this.getSessionSettings();
    if (!summary || !summary.creditSettings) return "";
    const availableTasks = summary.creditSettings.credits.filter((c) =>
      isAvailableItem(c)
    );
    let message = `Your session has ${availableTasks.length} tasks enabled, from which ${summary.creditSettings.dailyMandatoryCredits} will be selected daily.\n`;
    const preSelects = availableTasks.filter(
      (p) => p.preSelect !== undefined && p.preSelect !== PreselectType.none
    );
    if (preSelects.length > 0) {
      message += `There are ${preSelects.length} tasks configured to apply on specific days every week.\n\nThese are:\n`;
      preSelects.forEach(
        (p) => (message += `${p.name} (${PreselectType[p.preSelect]})\n`)
      );
      message += "\n";
    }

    const busyTasks = availableTasks.filter(
      (p) => p.availabilityDayType !== AvailabilityDayType.FreeDays
    );
    message += `${busyTasks.length} tasks are considered safe for busy days. This means these can still be selected on work days. All ${availableTasks.length} tasks are available for any day not marked as busy days in the main menu.\n`;
    message += "\n";
    let totalTaskCount =
      summary.creditSettings.dailyMandatoryCredits + preSelects.length;
    const events = summary.eventsSettings.events.filter((c) =>
      isAvailableItem(c)
    );
    if (!summary.eventsSettings.disableEvents) {
      let eventTaskCount = 0;
      events
        .filter((e) => e.availability === AvailabilityType.daily)
        .forEach((f) => {
          if (f.taskCategories)
            eventTaskCount += f.pickSingleCategoryTask
              ? 1
              : f.taskCategories.length;
          if (f.taskKeys)
            eventTaskCount += f.pickSingleTask ? 1 : f.taskKeys.length;
          if (f.penalties)
            eventTaskCount += f.pickSinglePenalty ? 1 : f.penalties.length;
          if (f.pillories)
            eventTaskCount += f.pickSinglePillory ? 1 : f.pillories.length;
        });
      totalTaskCount += eventTaskCount;
      message += `Including enabled events, up to ${totalTaskCount} tasks can be selected by the system for a day. This can vary based on times and randomness\n`;
    }
    message += `As an estimate, this means ${round(totalTaskCount * 0.5)} hours a day will be spent performing tasks for the owner.\n`;
    message += "\n";

    message += `Permissions are ${summary.permissionSettings.disablePermissions ? "disabled" : "enabled"
      }\n`;
    if (!summary.permissionSettings.disablePermissions) {
      message += `The session has ${summary.permissionSettings.permissions.filter((c) => isAvailableItem(c))
        .length
        } permissions decided daily\n`;
    }
    message += "\n";

    message += `Position Training is ${summary.positionSettings.disablePositions ? "disabled" : "enabled"
      }\n`;
    if (!summary.positionSettings.disablePositions) {
      message += `The session has at most ${summary.positionSettings.maxPositionCount
        } positions added daily. Which could result in ${summary.positionSettings.maxPositionCount *
        summary.positionSettings.positionUpperDuration
        } minutes per day spent holding positions.\n`;
    }
    message += "\n";

    message += `Events are ${summary.eventsSettings.disableEvents ? "disabled" : "enabled"
      }\n`;
    if (!summary.eventsSettings.disableEvents) {
      message += `There are ${events.length
        } events configured for the week, of which ${events.filter((e) => e.availability === AvailabilityType.daily).length
        } are daily.\n`;
    }
    message += "\n";

    message += `Daily Reporting is ${summary.dailyReporting ? "enabled" : "disabled"
      }\n`;
    if (summary.dailyReporting) {
      message += `The reports must be around ${summary.reportWordCount} words.\n`;
      if (summary.reportStyle !== ReportStyle.once) {
        message += "Also, reports are requested twice a day\n";
      } else {
        message += "Reports are only requested once a day\n";
      }
    }
    if (
      summary.weekendHandling === WeekendHandling.harsh ||
      summary.weekendHandling === WeekendHandling.easy
    ) {
      message += "\n";
      message += `The settings have weekends set to ${WeekendHandling[summary.weekendHandling]
        } and schedules will use this setting to generate those days activites using harder/easier tasks as indiciated.\n\n`;
    }
    return message;
  };

  public clearLogs = () => {
    void this.database.clearLogs(this.getControl().userid).catch((e) => {
      sendExceptionMessage(this, e);
      void this.auditUser(
        new AuditMessage(
          AuditActionTypes.Exception,
          "Error",
          JSON.stringify(e),
          TraceLevel.error
        )
      );
    });
  };

  public auditUser = (message: AuditMessage): void => {
    this.auditService.audit(message);
  };

  public updateVoteTask = (item: VoteTask) => {
    void this.database.updateVoteTask(item);
  };

  // eslint-disable-next-line require-await
  public async sendMessage(
    message: string,
    subject: string,
    fromUserid: string,
    fromUsername: string,
    isSystemAudit: boolean,
    sentReceiptRequired: boolean,
    messageType: MessageType,
    recipients: IUserRecord[],
    ignoreSelfOwned?: boolean,
    state?: any
  ): Promise<void> {
    void this.database.sendMessage(
      message,
      subject,
      fromUserid,
      fromUsername,
      isSystemAudit,
      sentReceiptRequired,
      messageType,
      recipients,
      ignoreSelfOwned,
      state
    );
  }

  public getSessionSchedule = (includeHidden: boolean): IDaySchedule[] => {
    return this.scheduleManager.getSessionSchedule(includeHidden);
  };

  public getScheduledCustomSchedule = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _day: EventDay
  ): ICustomSchedule | undefined => {
    return undefined;
  };

  public getDaySessionSchedule = (
    day: EventDay,
    includeHidden: boolean,
    ignoreAvailability: boolean,
    hidePendingItems: boolean
  ): IDaySchedule => {
    return this.scheduleManager.getDaySessionSchedule(
      day,
      includeHidden,
      ignoreAvailability,
      hidePendingItems
    );
  };

  public determineTasksForDay(
    day: EventDay,
    events: IEventItem[] | null,
    perms: IPermissionItem[] | null,
    includeHidden: boolean,
    ignoreAvailability: boolean,
    _hidePendingItems: boolean,
    ignoreScheduleTasks?: boolean
  ): ((ITaskItem | IPenaltyItem) & IScheduledInfo)[] {
    return this.scheduleManager.determineTasksForDay(
      day,
      events,
      perms,
      includeHidden,
      ignoreAvailability,
      _hidePendingItems,
      ignoreScheduleTasks
    );
  }

  protected peekWeekendDays = (): EventDay[] => {
    return [EventDay.saturday, EventDay.sunday];
  };

  public hasNewItems(): boolean {
    const items = this.getNewItems();
    return items.length > 0;
  }

  private newItemsFilter = (i: ISettingsItem) => {
    const viewingSystemRecord =
      this.isSystemLoggedOn() && this.isSystemSettings();
    return (
      !i.isDeleted &&
      ((viewingSystemRecord && i.requiresValidation === true) ||
        (!viewingSystemRecord &&
          i.requiresValidation !== true &&
          i.isNew === true))
    );
  };

  public getBusyDays(): EventDay[] {
    return [];
  }

  public getWeekendDays = (): EventDay[] => {
    return [];
  };

  public getNewItems = (): ISettingsItem[] => {
    if (!this.isLoaded()) return [];
    const newObjs = (
      (this.settings?.creditSettings?.credits.filter(
        this.newItemsFilter
      ) as ISettingsItem[]) ?? []
    )
      .concat(
        this.settings?.eventsSettings?.events.filter(this.newItemsFilter) ?? []
      )
      .concat(
        this.settings?.penaltySettings?.penalties.filter(this.newItemsFilter) ??
        []
      )
      .concat(
        this.settings?.permissionSettings?.permissions.filter(
          this.newItemsFilter
        ) ?? []
      )
      .concat(
        this.settings?.positionSettings?.positions.filter(
          this.newItemsFilter
        ) ?? []
      );

    return newObjs.map(o => cloneObject(o) as ISettingsItem);
  };

  public getShoppingItems(): IShoppingRequest[] {
    return [];
  }

  public unownSession = async (memberKey: string): Promise<void> => {
    const memberProfile = this.getProfile(memberKey);
    if (!memberProfile) return;
    if (this.isSystemLoggedOn()) {
      delete this.allControlRecords[memberProfile.userid];
      void this.database.deleteUser(memberKey);
      this.showMessage({
        message: "Deleted user " + memberProfile.username,
        persist: true,
        notificationType: NotificationType.General,
      });
      this.auditService.auditUserAction(
        `Unowned session for member key ${memberProfile.username}`,
        AuditActionTypes.Session
      );
      this.auditService.auditUserAction(
        `System Deleted member ${memberProfile.username}`,
        AuditActionTypes.User
      );
      return;
    }
    await this.unownSessionForUser(memberProfile);
  };

  public deleteUser = (id: string) => {
    if (this.isSystemLoggedOn()) {
      delete this.allControlRecords[id];
      void this.database.deleteUser(id);
    }
  };

  public unownSessionForUser = async (
    member: ControlRecord,
    suppressMessages?: boolean
  ): Promise<void> => {
    await this.database.removeOwnership(member);
    this.auditService.auditUserAction(
      `Unowned session for member ${member.username}`,
      AuditActionTypes.Session
    );
    if (!suppressMessages) {
      this.showMessage({
        message: "Unowned session for member key",
        persist: true,
        notificationType: NotificationType.General,
      });
      sendMessageToMember(
        MessageType.Unowned,
        this,
        "Your session has been unowned by " + this.controlRecord.username,
        "You are now unowned",
        [member]
      );
    }
  };

  public getOwnedSessions = (): ControlRecord[] => {
    if (this.isSystemSettings())
      return this.getAllControlRecords().filter((c) => c.username); // this removes virtual system profiles
    return this.getAllControlRecords().filter(
      (f) =>
        f.ownerHasAccess(this.controlRecord) &&
        f.userid !== this.controlRecord.userid
    );
  };

  public getActiveSessions = (
    includeOwners?: boolean
  ): Record<string, ControlRecord> => {
    const activeKeys = this.getAllControlRecords().filter(
      (u) =>
        this.systemUserId !== u.userid &&
        (includeOwners ||
          u.role === ProfileRole.Member ||
          u.role === ProfileRole.Switch) &&
        isAfter(u.lastUpdateTime, startOfYesterday())
    );
    const activeUsers: Record<string, ControlRecord> = {};
    activeKeys.forEach((u) => {
      activeUsers[u.userid] = u;
    });
    return activeUsers;
  };

  /**
   * Different from delete, we want to remove the user settings option for this item
   * leaving the parent item to set the details
   * @param item the item to remove from the user settings.
   */
  public removeOverrideItem = (item: ISettingsItem) => {
    const strippedSettings = stripSettings(
      this.settings,
      this.ownerSettings,
      this.systemSettings,
      this.isSystemSettings(),
      this.controlRecord.userid,
      this.database
    );
    const updated = this.itemRepository.removeOverride(strippedSettings, item);
    if (updated) {
      void this.database.updateSettings(
        this.controlRecord.userid,
        strippedSettings,
        true
      );
    }
    this.onSettingsChanged(strippedSettings);
  };

  private getMutableSettingsItem = (item: ISettingsItem): ISettingsItem | undefined => {
    switch (item.itemType) {
      case ItemTypes.task:
        return this.settings.creditSettings.credits.find((i) => i.key === item.key) as ISettingsItem | undefined;
      case ItemTypes.position:
        return this.settings.positionSettings.positions.find((i) => i.key === item.key) as ISettingsItem | undefined;
      case ItemTypes.permission:
        return this.settings.permissionSettings.permissions.find((i) => i.key === item.key) as ISettingsItem | undefined;
      case ItemTypes.event:
        return this.settings.eventsSettings.events.find((i) => i.key === item.key) as ISettingsItem | undefined;
      case ItemTypes.penalty:
        return this.settings.penaltySettings.penalties.find((i) => i.key === item.key) as ISettingsItem | undefined;
      default:
        return undefined;
    }
  };

  private applyReviewDecision = (
    items: ISettingsItem[],
    action: "approve" | "ignore" | "hide",
    mutate: (item: ISettingsItem) => void
  ): void => {
    const unresolved: ISettingsItem[] = [];

    items.forEach((item) => {
      const currentItem = this.getMutableSettingsItem(item);
      const target = currentItem ?? item;
      mutate(target);
      if (!currentItem) unresolved.push(target);
    });

    if (unresolved.length > 0) {
      void this.updateSettingsItems(unresolved);
      return;
    }

    void this.updateSettings(this.settings);
  };

  public approveNewItems = (items: ISettingsItem[]) => {
    this.applyReviewDecision(items, "approve", (item) => {
      item.hidden = false;
      item.isDeleted = false;
      item.isNew = false;
      item.requiresValidation = false;
    });
  };

  public ignoreNewItems = (items: ISettingsItem[]) => {
    this.applyReviewDecision(items, "ignore", (item) => {
      item.isDeleted = true;
      item.hidden = false;
      item.isNew = false;
      item.requiresValidation = false;
    });
  };

  public hideNewItems = (items: ISettingsItem[]) => {
    this.applyReviewDecision(items, "hide", (item) => {
      item.hidden = true;
      item.isDeleted = false;
      item.isNew = false;
      item.requiresValidation = false;
    });
  };

  public itemCreated = (newItem?: Item) => {
    // now we need to add this as a new credit
    if (!newItem) return;
    this.taskEditItem({
      isNew: true,
      requiresValidation: this.isSystemLoggedOn(),
      credit: 1,
      countType: newItem.countType ?? CountType.none,
      count: newItem.count,
      creditTaskType: TaskType.standard,
      name: newItem.name ?? "",
      description: newItem.description ?? "",
      img: newItem.image ?? "",
      imgId: "",
      itemType: ItemTypes.task,
      key: "",
      preSelect: PreselectType.none,
      alwaysShowTime: false,
      categories: newItem.categories ?? ["Custom Item"],
      isItem: true,
      options: [],
      availabilityDayType: newItem.availabilityDayType,
    });
  };

  public taskCreated = (newTask?: Task) => {
    // now we need to add this as a new credit
    if (!newTask) return;
    const settingsItem: ITaskItem = {
      isNew: true,
      requiresValidation: this.isSystemLoggedOn(),
      credit: 1,
      countType: newTask.countType ?? CountType.none,
      count: newTask.count,
      creditTaskType: TaskType.standard,
      name: newTask.name ?? "",
      description: newTask.description ?? "",
      img: newTask.image ?? "",
      imgId: "",
      itemType: ItemTypes.task,
      key: "",
      preSelect: PreselectType.none,
      alwaysShowTime: false,
      categories: newTask.categories ?? ["Custom Task"],
      itemsUsed: newTask.items ? [newTask.items.map((c) => c.key)] : [],
      isItem: false,
      options: [],
      availabilityDayType: newTask.availabilityDayType,
    };
    if (this.getControl().isOwned() && !this.canEditSettings()) {
      sendItemRequestSettingsChangeToOwner(this, settingsItem, "Member Suggestion");
      Emitter.emitNotification({ message: 'Request Sent to Owner', notificationType: NotificationType.SettingsInfo });
      return;
    }
    this.taskEditItem(settingsItem);
  };

  public getLovenseItems = (): (ITaskItem | IPenaltyItem)[] => {
    let items: (ITaskItem | IPenaltyItem)[] = [];
    items = this.getTasks(
      (t) => t.isLovenseToy === true || t.lovenseToyId !== undefined,
      false,
      true
    );
    items = items.concat(
      this.getPenalties(
        (t) => t.isLovenseToy === true || t.lovenseToyId !== undefined,
        false,
        true
      )
    );
    return items;
  };
  public undeleteItem = (item: ISettingsItem) => {
    this.itemRepository.undeleteItem(this.settings, item);
    void this.updateSettings(this.settings);
    this.showMessage({
      message: `Enabled ${item.name}`,
      notificationType: NotificationType.SettingsInfo,
    });
  };

  public showItem = (item: ISettingsItem, show: boolean) => {
    this.itemRepository.setItemVisibility(this.settings, item, show);
    void this.updateSettings(this.settings);
    this.showMessage({
      message: `${item.name} visibility updated`,
      notificationType: NotificationType.SettingsInfo,
    });
  };

  public getRandomLine = (request: ILinesRequest) => {
    let options = ["i am a slave and i must obey"];
    if (request.item.itemType === ItemTypes.penalty) {
      const pen = this.getPenalty(request.item.key);
      if (pen && pen.options && pen.options.length > 0) options = pen.options;
    } else {
      const task = this.getTask(request.item.key);
      if (task && task.options && task.options.length > 0)
        options = task.options;
    }
    return getRandomItem(options);
  };

  public canHandleDiscordProofCalls = () => {
    return (
      canHandleDiscordCalls(this.getConfig(), this.controlRecord.username) &&
      !(
        this.getControl().disablePublicProofs ||
        this.getGeneralSettings().disableDiscordProof
      )
      && import.meta.env.PROD
    );
  };

  public createPilloryTopic = (itemToAdd: AdditionDialogResponse) => {
    if (!itemToAdd) return;

    const options: ItemAdditionOptions = {
      expectedCount: itemToAdd.expectedCount,
      countType: itemToAdd.countType,
      reason: itemToAdd.reason,
      selectedCondition: itemToAdd.selectedCondition,
      selectedChoiceName: itemToAdd.selectedChoiceName,
      selectedItemGroupOptions: itemToAdd.selectedItems,
      selectedItemGroupKeys: itemToAdd.selectedItemsGroupKeys,
      ownerAdded: true,
      ownerName: this.isSystemLoggedOn()
        ? this.controlRecord.getOwner().username
        : this.cookies.username,
      isPillory: true,
      lovenseToyId:
        itemToAdd.lovenseToyId ?? (itemToAdd.item as ILovenseToy).lovenseToyId,
      lovenseProfile:
        itemToAdd.lovenseProfile ??
        (itemToAdd.item as ILovenseToy).lovenseProfile,
      voteCount:
        itemToAdd.voteCount ?? (itemToAdd.item as ILovenseToy).voteCount,
    };

    // const pilloryCopy = {...itemToAdd.item} as IPenaltyItem;
    // this let's us list the items they own if there isn't a set of options to pick from on the vote
    // pilloryCopy.options = pilloryCopy.options || this.getOwnedItems()[pilloryCopy.key] || [];

    const pendingItem: IPendingItem = {
      item: itemToAdd.item,
      itemAdditionOptions: options,
      updateType: PendingUpdateType.add,
      date: itemToAdd.date.toISOString(),
    };
    this.updatePilloryTopicFromPendingItem(pendingItem);
  };

  public updatePilloryTopicFromPendingItem = (penalty: IPendingItem) => {
    if (!penalty.item) return;
    const topic = new PilloryTopic({
      ...penalty,
      name: penalty.item.name,
      reason: penalty.itemAdditionOptions?.reason || "",
      username: this.getControl().username,
      userid: this.getControl().userid,
      ownername: this.getControl().getOwner().username,
      countType: penalty.itemAdditionOptions?.countType || CountType.minutes,
      incrementCount: (penalty.itemAdditionOptions?.voteCount ??
        penalty.itemAdditionOptions?.expectedCount) as number,
      pilloryEndTime: penalty.date as string,
      isRoulette: false,
      currentTotal: (penalty.itemAdditionOptions?.voteCount ??
        penalty.itemAdditionOptions?.expectedCount) as number,
      votedMembers: [],
      voteOptions: {},
    });
    void this.updatePilloryTopic(topic);
    this.showMessage({
      message: `Pillory: ${topic.name} rewarded to ${topic.username}`,
      notificationType: NotificationType.PilloryInfo,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public createPreview = (force: boolean): IBasket => {
    return {} as IBasket;
  };

  public updatePilloryTopic = (topic: PilloryTopic) => {
    void this.database.updatePilloryTopic(topic);
    // determine if this task has been started within the last minute of the last task
    handleDiscordPilloryTicker(this, topic);
    void this.auditUser(
      new UserActionAuditMessage(
        AuditActionTypes.Session,
        "Pillory Topic Created",
        `${topic.name} created`
      )
    );
    sendPilloryAddedMessageToOwner(this, topic);
    this.handlePilloryPreviewItems();
  };

  public handlePilloryPreviewItems = () => {
    return;
  };

  /**
   * This intentionally does not charge the member but just allocates the money to the owner as the user will pay a one off fee of all charges when initialisation has completed
   * @param amount
   * @returns
   */
  public payOwner = (amount: number): void => {
    const control = this.getControl();
    if (!control.isOwned() || control.getOwner().userid === control.userid)
      return;
    void this.database.payOwner(control.getOwner().userid, amount);
    this.auditService.auditUserAction(
      `Owner Bank Updated by £${amount}`,
      AuditActionTypes.Session
    );
  };

  public auditBankChange(reason: string, change: number): number {
    let newBank = this.controlRecord.bank;
    newBank += change;
    newBank = Number(newBank.toFixed(2));
    if (isNaN(newBank)) return 0;
    if (newBank < 0) newBank = 0;
    this.updateControlAttributes({ bank: newBank });
    this.database.auditBankChange(
      this.controlRecord.bank,
      `${reason}: ${change}`,
      this.controlRecord.userid
    );
    return this.auditService.auditBankChange(reason, change, newBank);
  }
  // eslint-disable-next-line require-await
  public findItemImage = async (
    item: IOptionSupport & IPictureItem,
    defaultImage: string
  ): Promise<string> => {
    const latestItem = this.getCurrentItem(
      item as ISettingsItem
    ) as IOptionSupport & IPictureItem & INamedItem;
    if (
      latestItem &&
      latestItem.options &&
      latestItem.optionImages &&
      item.selectedCondition
    ) {
      const idx = latestItem.options.findIndex(
        (i) =>
          item.selectedCondition &&
          item.selectedCondition.replace(
            ` ${(item as any as INamedItem).name}`,
            ""
          ) === i
      );
      if (
        idx > -1 &&
        latestItem.optionImages[idx] &&
        latestItem.optionImages[idx].imgId
      ) {
        return this.getDisplayImage(latestItem.optionImages[idx], defaultImage);
      }
    }
    return this.getDisplayImage(item, defaultImage);
  };

  public getAvailableRandomTasks = (
    intraDay: boolean,
    date: Date
  ): TaskItem[] => {
    return [];
  };

  public getOverrideTimes(): Record<EventDay, number[]> {
    return this.getState<Record<EventDay, number[]>>(
      STATE_KEY_OVERRIDE_TIMES,
      {} as Record<EventDay, number[]>
    );
  }
}
